import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getTradeGroupDetails, getBollingerEntryConditions, getRsiExtremesEntryConditions, getMacdEntryConditions } from '../api/tradeGroups';
import OrdersTable from './OrdersTable';
import SinglePositionCycleHistory from './SinglePositionCycleHistory';
import StandaloneSection from './shared/StandaloneSection';
import CancelTradeGroupModal from './CancelTradeGroupModal';
import OperatorPauseResumeModal from './OperatorPauseResumeModal';
import VolatilityAttributesSection from './VolatilityAttributesSection';
import OrchestratedLadderSection from './OrchestratedLadderSection';
import TradeGroupTabs, { TradeGroupTabId } from './TradeGroupTabs';
import AdjustmentLogTable from './AdjustmentLogTable';
import LadderRangesTable from './LadderRangesTable';
import TimelineChart from './TimelineChart';
import TradeActivityTimeline from './TradeActivityTimeline';
import PauseInformationPanel from './PauseInformationPanel';
import GranularityDateRangeControls, { TIMEFRAMES, TimeframeKey } from './shared/GranularityDateRangeControls';
import { useDateRange, useDateRangeStore } from '../stores/useDateRangeStore';
import { getDefaultDateRange } from '../utils/dateRangeDefaults';
import GaugeComponent from 'react-gauge-component';
import RsiExtremesExitConditionsBar from './indicators/RsiExtremesExitConditionsBar';
import BollingerExitConditionsBar from './indicators/BollingerExitConditionsBar';
import MacdExitConditionsBar from './indicators/MacdExitConditionsBar';
import VwapEntryConditionsBar from './indicators/VwapEntryConditionsBar';
import VwapExitConditionsBar from './indicators/VwapExitConditionsBar';
import IchimokuEntryConditionsBar from './indicators/IchimokuEntryConditionsBar';
import IchimokuExitConditionsBar from './indicators/IchimokuExitConditionsBar';
import PivotPointEntryConditionsBar from './indicators/PivotPointEntryConditionsBar';
import PivotPointExitConditionsBar from './indicators/PivotPointExitConditionsBar';
import MaCrossoverChart from './charts/MaCrossoverChart';
import './shared/MetricCards.css';
import './TradeGroupDetail.css';
import { useGlobalAutoRefreshStore, useAutoRefreshTrigger } from '../stores/useGlobalAutoRefreshStore';

/** The 8 single-position framework types render the cycle-based view instead of the ladder order table (REQ-009). */
const SINGLE_POSITION_TYPES = new Set([
  'BOLLINGER_REVERSION', 'RSI_EXTREMES', 'MACD_REVERSION', 'MACD_MOMENTUM',
  'VWAP_REVERSION', 'ICHIMOKU_CLOUD', 'PIVOT_POINT_TRADING', 'MA_CROSSOVER',
  'SUPPORT_RETEST_DIP_LADDER',
]);

const TradeGroupDetail: React.FC = () => {
  const { tradeGroupId } = useParams<{ tradeGroupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const setActiveDashboard = useGlobalAutoRefreshStore((state) => state.setActiveDashboard);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pauseResumeAction, setPauseResumeAction] = useState<'pause' | 'resume' | null>(null);
  const [activeTab, setActiveTab] = useState<TradeGroupTabId>('orders');
  const [selectedTimeframe, setSelectedTimeframe] = useState<TimeframeKey>('15m'); // Default to 15m for better context
  const refreshTrigger = useAutoRefreshTrigger('trade-group-detail');

  // Date range from store
  const { dateRange } = useDateRange();
  const { setCustomRange } = useDateRangeStore();
  
  // Granularity in minutes
  const granularity = useMemo(() => TIMEFRAMES[selectedTimeframe].granularityMins, [selectedTimeframe]);

  // Initialize date range when granularity changes
  useEffect(() => {
    const { startTime, endTime } = getDefaultDateRange(granularity);
    setCustomRange({
      start_date: startTime.split('T')[0],
      end_date: endTime.split('T')[0],
    });
  }, [granularity, setCustomRange]);

  // Set this view as active when mounted, clear when unmounted
  useEffect(() => {
    setActiveDashboard('trade-group-detail');
    return () => setActiveDashboard(null);
  }, [setActiveDashboard]);

  // When global refresh trigger changes, invalidate queries to refetch data
  useEffect(() => {
    if (refreshTrigger > 0) {
      queryClient.invalidateQueries({ queryKey: ['trade-group-details', tradeGroupId] });
      queryClient.invalidateQueries({ queryKey: ['bollinger-entry-conditions', tradeGroupId] });
      queryClient.invalidateQueries({ queryKey: ['macd-entry-conditions', tradeGroupId] });
    }
  }, [refreshTrigger, queryClient, tradeGroupId]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trade-group-details', tradeGroupId],
    queryFn: () => getTradeGroupDetails(tradeGroupId!),
    enabled: !!tradeGroupId,
    retry: 2,
  });

  // Fetch Bollinger entry conditions for BOLLINGER_REVERSION trade groups
  const { data: entryConditions } = useQuery({
    queryKey: ['bollinger-entry-conditions', tradeGroupId],
    queryFn: () => getBollingerEntryConditions(tradeGroupId!),
    enabled: !!tradeGroupId && data?.tradeGroup?.tradeGroupType === 'BOLLINGER_REVERSION',
    refetchInterval: 30000, // Refresh every 30 seconds for near-real-time updates
    retry: 1,
  });

  // Fetch RSI Extremes entry conditions for RSI_EXTREMES trade groups
  // Fetch for all states to get current RSI value
  const { data: rsiEntryConditions, isLoading: isRsiLoading, isError: isRsiError, error: rsiError } = useQuery({
    queryKey: ['rsi-entry-conditions', tradeGroupId],
    queryFn: () => getRsiExtremesEntryConditions(tradeGroupId!),
    enabled: !!tradeGroupId && data?.tradeGroup?.tradeGroupType === 'RSI_EXTREMES',
    refetchInterval: 30000, // Refresh every 30 seconds for near-real-time updates
    retry: 1,
  });

  // Fetch MACD entry conditions for MACD_REVERSION trade groups
  const { data: macdEntryConditions, isLoading: isMacdLoading } = useQuery({
    queryKey: ['macd-entry-conditions', tradeGroupId],
    queryFn: () => getMacdEntryConditions(tradeGroupId!),
    enabled: !!tradeGroupId && data?.tradeGroup?.tradeGroupType === 'MACD_REVERSION',
    refetchInterval: 30000,
    retry: 1,
  });

  // Debug logging for RSI entry conditions
  if (data?.tradeGroup?.tradeGroupType === 'RSI_EXTREMES' && data?.tradeGroup?.state === 'WATCHING') {
    console.log('RSI Entry Conditions Query State:', {
      isLoading: isRsiLoading,
      isError: isRsiError,
      error: rsiError,
      hasData: !!rsiEntryConditions,
      data: rsiEntryConditions
    });
  }

  if (isLoading) {
    return (
      <div className="trade-group-detail">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading trade group details...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="trade-group-detail">
        <div className="error-state">
          <h2>Error Loading Trade Group</h2>
          <p>{error instanceof Error ? error.message : 'Unknown error occurred'}</p>
          <div className="error-actions">
            <button onClick={() => refetch()} className="btn-primary">
              Retry
            </button>
            <button onClick={() => navigate('/trade-groups')} className="btn-secondary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { tradeGroup, orders } = data;

  // Calculate some derived metrics
  const buyOrders = orders.filter(o => o.side === 'BUY');
  const openBuyOrders = buyOrders.filter(o => o.status === 'OPEN');
  const filledBuyOrders = buyOrders.filter(o => o.status === 'FILLED');

  const formatCurrency = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '$0.00';
    // For sub-$1 assets (e.g., ADA at $0.24-ish), enforce 4 decimals so trailing
    // zeros render (e.g., $0.2400 instead of $0.24). Matches VwapExitConditionsBar
    // and what operators expect for cents-precision instruments.
    const minDigits = Math.abs(value) < 1 ? 4 : 2;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: minDigits,
      maximumFractionDigits: 4,
    }).format(value);
  };

  const formatPercent = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '0.00%';
    return `${(value * 100).toFixed(2)}%`;
  };

  const formatStopBasis = (basis?: string | null): string => {
    if (basis === null || basis === undefined) return 'N/A';
    switch (basis) {
      case 'ATR':
        return 'ATR-scaled';
      case 'FLAT_FALLBACK':
        return 'Flat %';
      default:
        // Additive-enum-safe: a future StopBasis value renders as its raw name.
        return basis;
    }
  };

  const formatStopTier = (tier?: string | null): string | null => {
    if (tier === null || tier === undefined) return null;
    switch (tier) {
      case 'GROUP':
        return 'Group override';
      case 'PRODUCT':
        return 'Product default';
      case 'GLOBAL':
        return 'Global default';
      default:
        // Additive-enum-safe: a future ConfigResolutionTier value renders as its raw name.
        return tier;
    }
  };

  const getProfitClass = (value: number | null | undefined) => {
    if (!value || value === 0) return '';
    return value > 0 ? 'profit-positive' : 'profit-negative';
  };

  const getStatusBadgeClass = (state: string) => {
    // Convert state to CSS class format: LOSS_PROTECTION -> status-loss-protection
    return `status-${state.toLowerCase().replace(/_/g, '-')}`;
  };

  const calculatePositionInfo = (minPrice: number, maxPrice: number, currentPrice: number) => {
    const configuredRange = maxPrice - minPrice;

    if (currentPrice < minPrice) {
      // Below range
      const pctBelow = ((minPrice - currentPrice) / minPrice) * 100;
      return {
        label: '% Below Range',
        value: pctBelow.toFixed(2) + '%',
        status: 'below'
      };
    } else if (currentPrice > maxPrice) {
      // Above range
      const pctAbove = ((currentPrice - maxPrice) / maxPrice) * 100;
      return {
        label: '% Above Range',
        value: pctAbove.toFixed(2) + '%',
        status: 'above'
      };
    } else {
      // Within range
      const pctInRange = ((currentPrice - minPrice) / configuredRange) * 100;
      return {
        label: '% in Range',
        value: pctInRange.toFixed(2) + '%',
        status: 'within'
      };
    }
  };

  // Helper function to calculate position for Bollinger Bands visualization
  const calculateBandPosition = (price: number | undefined, minPrice: number | undefined, maxPrice: number | undefined) => {
    if (!price || !minPrice || !maxPrice) return 0;
    const range = maxPrice - minPrice;
    if (range === 0) return 50;
    const position = ((price - minPrice) / range) * 100;
    return Math.max(0, Math.min(100, position)); // Clamp between 0-100%
  };

  const renderPriceRuler = (
    minPrice: number,
    maxPrice: number,
    currentPrice: number,
    type: 'buy' | 'sell'
  ) => {
    // Calculate the full range to display
    const displayMin = Math.min(currentPrice, minPrice);
    const displayMax = Math.max(currentPrice, maxPrice);
    const displayRange = displayMax - displayMin;

    // Calculate positions as percentages
    const rangeStart = ((minPrice - displayMin) / displayRange) * 100;
    const rangeEnd = ((maxPrice - displayMin) / displayRange) * 100;
    const currentPosition = ((currentPrice - displayMin) / displayRange) * 100;

    return (
      <div className="price-ruler">
        <div className="ruler-track">
          {/* Highlighted range zone */}
          <div
            className={`range-highlight ${type}`}
            style={{
              left: `${rangeStart}%`,
              width: `${rangeEnd - rangeStart}%`
            }}
            title={`${type === 'buy' ? 'Buy' : 'Sell'} Range: ${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`}
          ></div>

          {/* Range boundary markers */}
          <div
            className="range-marker range-marker-start"
            style={{ left: `${rangeStart}%` }}
            title={`Min ${type === 'buy' ? 'Buy' : 'Sell'} Price: ${formatCurrency(minPrice)}`}
          ></div>
          <div
            className="range-marker range-marker-end"
            style={{ left: `${rangeEnd}%` }}
            title={`Max ${type === 'buy' ? 'Buy' : 'Sell'} Price: ${formatCurrency(maxPrice)}`}
          ></div>

          {/* Current price indicator */}
          <div
            className={`current-price-marker ${type}`}
            style={{ left: `${currentPosition}%` }}
            title={`Current Price: ${formatCurrency(currentPrice)}`}
          >
            <div className="price-circle"></div>
            <div className="price-line"></div>
            <div className="price-label">Now</div>
          </div>
        </div>
        <div className="ruler-labels">
          <span className="ruler-label-start">{formatCurrency(displayMin)}</span>
          <span className="ruler-label-end">{formatCurrency(displayMax)}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="trade-group-detail">
      <button onClick={() => navigate('/trade-groups')} className="back-button">
        ← Back to Dashboard
      </button>

      {/* Pause Information Panel - RSI-PAUSE-010: Only shown when trade group is PAUSED */}
      {tradeGroup.state === 'PAUSED' && tradeGroup.lastPauseTime && (
        <PauseInformationPanel
          pauseTime={tradeGroup.lastPauseTime}
          pauseReason={tradeGroup.lastPauseReason || 'No reason provided'}
          pauseCount={tradeGroup.pauseCount || 0}
          peakCumulativePnl={tradeGroup.peakCumulativePnl}
          currentCumulativePnl={
            tradeGroup.tradeGroupType === 'RSI_EXTREMES'
              ? tradeGroup.rsiCumulativePnl
              : tradeGroup.tradeGroupType === 'BOLLINGER_REVERSION'
              ? tradeGroup.bollingerCumulativePnl
              : tradeGroup.tradeGroupType === 'MACD_REVERSION'
              ? tradeGroup.macdCumulativePnl
              : tradeGroup.tradeGroupType === 'VWAP_REVERSION'
              ? tradeGroup.vwapCumulativePnl
              : undefined
          }
          // Extract metrics from pause reason if available
          // TODO: In future, these will come from dedicated API fields
          winRate={
            tradeGroup.tradeGroupType === 'RSI_EXTREMES'
              ? tradeGroup.rsiWinRate
              : tradeGroup.tradeGroupType === 'BOLLINGER_REVERSION'
              ? tradeGroup.bollingerWinRate
              : tradeGroup.tradeGroupType === 'MACD_REVERSION'
              ? tradeGroup.macdWinRate
              : tradeGroup.tradeGroupType === 'VWAP_REVERSION'
              ? tradeGroup.vwapWinRate
              : undefined
          }
          // Resume eligibility data (will be populated by backend in future iterations)
          // For now, these are undefined and the panel will show "pending" state
          minPauseDurationMet={undefined}
          marketRegimeFavorable={undefined}
          volatilityPercentile={undefined}
          trendStrength={undefined}
        />
      )}

      {/* Operator Pause Information Panel - REQ-013: shown for OPERATOR_PAUSED groups.
          Distinct from the automated PAUSED panel — copy states the group will NOT
          auto-resume and must be resumed manually. Reason/time may be NULL (non-RSI). */}
      {tradeGroup.state === 'OPERATOR_PAUSED' && (
        <PauseInformationPanel
          operatorPaused
          pauseTime={tradeGroup.lastPauseTime}
          pauseReason={tradeGroup.lastPauseReason}
          pauseCount={tradeGroup.pauseCount || 0}
        />
      )}

      {/* Main Card Container */}
      <StandaloneSection
          title ={`${tradeGroup.name} (${tradeGroup.productId})${entryConditions?.strategy_metric_period ? ` - ${entryConditions.strategy_metric_period}` : ''}`} actions={
          <div className="header-right">
            {/* Operator pause control: shown when the group is in an active/pausable state
                (REQ-014). Excludes already-paused and terminal states. */}
            {['WATCHING', 'IN_POSITION', 'PARTIAL_EXIT'].includes(tradeGroup.state) && (
              <button
                className="modal-pill-button modal-pill-primary"
                onClick={() => setPauseResumeAction('pause')}
                title="Operator-pause this trade group"
              >
                Pause
              </button>
            )}
            {/* Operator resume control: shown only for OPERATOR_PAUSED groups (REQ-014). */}
            {tradeGroup.state === 'OPERATOR_PAUSED' && (
              <button
                className="modal-pill-button modal-pill-primary"
                onClick={() => setPauseResumeAction('resume')}
                title="Resume this operator-paused trade group"
              >
                Resume
              </button>
            )}
            <button
              className="pill-cancel-button"
              onClick={() => setShowCancelModal(true)}
              title="Cancel trade group orders"
            >
              Cancel
            </button>
            <span className={`status-badge ${getStatusBadgeClass(tradeGroup.state)}`}>
              {tradeGroup.state.replace(/_/g, ' ')}
            </span>
          </div>
      }>

        {/* Card Body */}
        <div className="card-body">
          {/* Hero Metrics - Primary Performance Indicators */}
          <div className="hero-metrics">
            <div className="hero-metric hero-metric-primary">
              <div className="hero-label">Current Price</div>
              <div className="hero-value hero-value-price">
                {formatCurrency(tradeGroup.currentPrice)}
              </div>
              <div className="hero-change hero-change-muted">{tradeGroup.productId}</div>
            </div>

            <div className="hero-metric">
              <div className="hero-label">Overall P&L</div>
              <div className={`hero-value ${getProfitClass(tradeGroup.combinedPnl)}`}>
                {formatCurrency(tradeGroup.combinedPnl)}
              </div>
              <div className="hero-change">Total Profit/Loss</div>
            </div>

            <div className="hero-metric">
              <div className="hero-label">Realized P&L</div>
              <div className={`hero-value ${getProfitClass(tradeGroup.totalCapturedProfit)}`}>
                {formatCurrency(tradeGroup.totalCapturedProfit)}
              </div>
              <div className="hero-change">Completed Trades</div>
            </div>

            <div className="hero-metric">
              <div className="hero-label">Unrealized P&L</div>
              <div className={`hero-value ${getProfitClass(tradeGroup.openSellUnrealizedProfit)}`}>
                {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
              </div>
              <div className="hero-change">Open Orders</div>
            </div>
          </div>

          {/* Secondary Metrics Grid */}
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-content">
                <div className="metric-label">Target P&L</div>
                <div className={`metric-value ${getProfitClass(tradeGroup.totalCapturedProfit + tradeGroup.openSellTargetProfit)}`}>
                  {formatCurrency(tradeGroup.totalCapturedProfit + tradeGroup.openSellTargetProfit)}
                </div>
              </div>
              <div className="metric-footer">Captured + Open Targets</div>
            </div>

            <div className="metric-card">
              <div className="metric-content">
                <div className="metric-label">Allocated Capital</div>
                <div className="metric-value">{formatCurrency(tradeGroup.originalQuoteSize || tradeGroup.currentQuoteSize)}</div>
              </div>
              <div className="metric-footer">
                Deployed {formatCurrency(tradeGroup.openBuyCostBasis + tradeGroup.openSellCostBasis)} · Held {formatCurrency(tradeGroup.netHoldingsCostBasis)}
              </div>
            </div>

            <div className="metric-card">
              <div className="metric-content">
                <div className="metric-label">Open Buy Cost</div>
                <div className="metric-value">{formatCurrency(tradeGroup.openBuyCostBasis)}</div>
              </div>
              <div className="metric-footer">{openBuyOrders.length} open orders</div>
            </div>

            <div className="metric-card">
              <div className="metric-content">
                <div className="metric-label">Open Sell Cost</div>
                <div className="metric-value">{formatCurrency(tradeGroup.openSellCostBasis)}</div>
              </div>
              <div className="metric-footer">{filledBuyOrders.length} filled buys</div>
            </div>

            <div className="metric-card">
              <div className="metric-content">
                <div className="metric-label">Avg Return</div>
                <div className={`metric-value ${getProfitClass(tradeGroup.avgCapturedReturn)}`}>
                  {formatPercent(tradeGroup.avgCapturedReturn)}
                </div>
              </div>
              <div className="metric-footer">Per completed trade</div>
            </div>
          </div>

          {/* Conditional: Show Bollinger indicators OR Price Ranges */}
          {tradeGroup.tradeGroupType === 'BOLLINGER_REVERSION' && tradeGroup.state === 'WATCHING' ? (
            /* Bollinger Reversion Indicators Dashboard - Only shown when WATCHING for entry */
            <div className="bollinger-indicators-dashboard">
              <h3 className="section-title">Technical Indicators & Entry Conditions</h3>
              
              {/* Gauges Row - Ordered: RSI → Market Regime → Bollinger Position */}
              <div className="gauges-row">
                {/* RSI Gauge - Momentum indicator (leftmost) */}
                <div className="gauge-container" title={`RSI Value: ${entryConditions?.rsi?.toFixed(2) ?? 'N/A'}`}>
                  <h4 className="gauge-title">RSI</h4>
                  <GaugeComponent
                    type="semicircle"
                    arc={{
                      colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                      padding: 0.02,
                      subArcs: [
                        { limit: 35, color: '#10b981' },
                        { limit: 70, color: '#f59e0b' },
                        { limit: 100, color: '#ef4444' }
                      ]
                    }}
                    pointer={{
                      type: 'blob',
                      animationDelay: 0,
                      elastic: true,
                      color: '#ffffff'
                    }}
                    value={entryConditions?.rsi ?? 50}
                    minValue={0}
                    maxValue={100}
                    labels={{
                      valueLabel: {
                        formatTextValue: (value: number) => value.toFixed(1),
                        style: { fontSize: '2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                      },
                      tickLabels: {
                        type: 'inner',
                        ticks: [
                          { value: 35 },
                          { value: 70 }
                        ],
                        defaultTickValueConfig: {
                          style: { fontSize: '0.75rem', fill: '#94a3b8' },
                          formatTextValue: (value: number) => value.toString()
                        }
                      }
                    }}
                  />
                  <p className="gauge-description">
                    {entryConditions?.entry_conditions?.is_rsi_oversold
                      ? '✓ Oversold - Entry signal'
                      : (entryConditions?.rsi ?? 50) > 70
                      ? '⚠ Overbought'
                      : 'Neutral'}
                  </p>
                </div>

                {/* Market Regime Gauge - Sets overall context (center) */}
                <div className="gauge-container">
                  <h4 className="gauge-title">Market Regime</h4>
                  <GaugeComponent
                    type="semicircle"
                    arc={{
                      colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                      padding: 0.02,
                      subArcs: [
                        { limit: 40, color: '#10b981' },  // Green: RANGING (16) and REVERSAL_POTENTIAL (28)
                        { limit: 70, color: '#f59e0b' },  // Yellow: TRENDING (50)
                        { limit: 100, color: '#ef4444' }   // Red: VOLATILE (84)
                      ]
                    }}
                    pointer={{
                      type: 'blob',
                      animationDelay: 0,
                      elastic: true,
                      color: '#ffffff'
                    }}
                    value={
                      entryConditions?.market_regime === 'RANGING' ? 16 :
                      entryConditions?.market_regime === 'REVERSAL_POTENTIAL' ? 28 :
                      entryConditions?.market_regime === 'TRENDING' ||
                      entryConditions?.market_regime === 'TRENDING_UP' ||
                      entryConditions?.market_regime === 'TRENDING_DOWN' ? 50 :
                      entryConditions?.market_regime === 'VOLATILE' ? 84 : 50
                    }
                    minValue={0}
                    maxValue={100}
                    labels={{
                      valueLabel: {
                        formatTextValue: () => entryConditions?.market_regime || 'UNKNOWN',
                        style: { fontSize: '1.2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                      },
                      tickLabels: {
                        type: 'inner',
                        ticks: [
                          { value: 16, valueConfig: { formatTextValue: () => 'RANGING' } },
                          { value: 28, valueConfig: { formatTextValue: () => 'REVERSAL' } },
                          { value: 50, valueConfig: { formatTextValue: () => 'TRENDING' } },
                          { value: 84, valueConfig: { formatTextValue: () => 'VOLATILE' } }
                        ],
                        defaultTickValueConfig: {
                          style: { fontSize: '0.6rem', fill: '#94a3b8' }
                        }
                      }
                    }}
                  />
                  <p className="gauge-description">
                    {entryConditions?.entry_conditions?.is_ranging_market
                      ? `✓ ${entryConditions?.market_regime === 'REVERSAL_POTENTIAL' ? 'Reversal setup' : 'Ideal for mean reversion'}`
                      : entryConditions?.market_regime === 'VOLATILE'
                      ? '✗ Avoid entry'
                      : '⚠ Use caution'}
                  </p>
                </div>

                {/* Bollinger Bands Gauge - Price position relative to bands (rightmost) */}
                <div className="gauge-container bollinger-gauge" title={`Current Price: ${formatCurrency(entryConditions?.current_price ?? tradeGroup.currentPrice)}`}>
                  <h4 className="gauge-title">Bollinger Position</h4>
                  <GaugeComponent
                    type="semicircle"
                    arc={{
                      colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                      padding: 0.02,
                      subArcs: [
                        { limit: 30, color: '#10b981' },
                        { limit: 70, color: '#f59e0b' },
                        { limit: 100, color: '#ef4444' }
                      ]
                    }}
                    pointer={{
                      type: 'blob',
                      animationDelay: 0,
                      elastic: true,
                      color: '#ffffff'
                    }}
                    value={entryConditions?.price_position_pct ?? 50}
                    minValue={0}
                    maxValue={100}
                    labels={{
                      valueLabel: {
                        formatTextValue: (value: number) => `${value.toFixed(0)}%`,
                        style: { fontSize: '1.5rem', fill: '#f1f5f9', fontWeight: 'bold' }
                      },
                      tickLabels: {
                        type: 'inner',
                        ticks: [
                          { value: 15, valueConfig: { formatTextValue: () => 'Lower' } },
                          { value: 85, valueConfig: { formatTextValue: () => 'Upper' } }
                        ],
                        defaultTickValueConfig: {
                          style: { fontSize: '0.65rem', fill: '#94a3b8' }
                        }
                      }
                    }}
                  />
                  <p className="gauge-description">
                    {entryConditions?.entry_conditions?.is_price_at_lower_band
                      ? '✓ At lower band - Entry condition'
                      : (entryConditions?.price_position_pct ?? 50) >= 50
                      ? 'Above middle band'
                      : 'Between bands'}
                  </p>
                </div>
              </div>

              {/* Entry Conditions Summary - Ordered: RSI, Volume, Market, S2 Pivot, Bollinger Band */}
              {entryConditions && (
                <div className="entry-conditions-summary">
                  <h4>Entry Conditions ({entryConditions.entry_conditions.conditions_met_count}/5 met)</h4>
                  <div className="conditions-grid">
                    {/* RSI condition first */}
                    <div className={`condition-item ${entryConditions.entry_conditions.is_rsi_oversold ? 'met' : 'not-met'}`}>
                      <span className="condition-icon">{entryConditions.entry_conditions.is_rsi_oversold ? '✓' : '✗'}</span>
                      <span className="condition-label">RSI Oversold (&lt;35)</span>
                    </div>
                    {/* Volume condition */}
                    <div className={`condition-item ${entryConditions.entry_conditions.is_volume_normal ? 'met' : 'not-met'}`}>
                      <span className="condition-icon">{entryConditions.entry_conditions.is_volume_normal ? '✓' : '✗'}</span>
                      <span className="condition-label">Normal Volume</span>
                    </div>
                    {/* Market regime condition */}
                    <div className={`condition-item ${entryConditions.entry_conditions.is_ranging_market ? 'met' : 'not-met'}`}>
                      <span className="condition-icon">{entryConditions.entry_conditions.is_ranging_market ? '✓' : '✗'}</span>
                      <span className="condition-label">Mean Reversion Market</span>
                    </div>
                    {/* S2 Pivot condition */}
                    <div className={`condition-item ${entryConditions.entry_conditions.is_above_pivot_support ? 'met' : 'not-met'}`}>
                      <span className="condition-icon">{entryConditions.entry_conditions.is_above_pivot_support ? '✓' : '✗'}</span>
                      <span className="condition-label">Above S2 Pivot</span>
                    </div>
                    {/* Bollinger condition last */}
                    <div className={`condition-item bollinger-condition ${entryConditions.entry_conditions.is_price_at_lower_band ? 'met' : 'not-met'}`}>
                      <span className="condition-icon">{entryConditions.entry_conditions.is_price_at_lower_band ? '✓' : '✗'}</span>
                      <span className="condition-label">Price at Lower Band</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Additional Details Grid - Ordered to align with gauges above */}
              <div className="bollinger-details-grid">
                {/* Trading Parameters - General info first */}
                <div className="detail-card">
                  <h4>Trading Parameters</h4>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">Granularity</span>
                      <span className="detail-value">{entryConditions?.granularity_mins ? `${entryConditions.granularity_mins} min` : 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Trend Strength</span>
                      <span className="detail-value">{entryConditions?.trend_strength?.toFixed(2) ?? 'N/A'}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Relative Volume</span>
                      <span className="detail-value">{entryConditions?.relative_volume?.toFixed(2) ?? 'N/A'}x</span>
                    </div>
                  </div>
                </div>

                {/* S2 Pivot Level - Support info in center */}
                {entryConditions?.s2_pivot_level && (
                  <div className="detail-card">
                    <h4>S2 Pivot Level</h4>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <span className="detail-label">Support Level</span>
                        <span className="detail-value">{formatCurrency(entryConditions.s2_pivot_level)}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Pivot Timeframe</span>
                        <span className="detail-value">{entryConditions.pivot_timeframe}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Distance</span>
                        <span className="detail-value">
                          {entryConditions.s2_pivot_level && entryConditions.current_price
                            ? `${((Math.abs(entryConditions.current_price - entryConditions.s2_pivot_level) / entryConditions.s2_pivot_level) * 100).toFixed(2)}%`
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Bollinger Bands Values - Rightmost to align with Bollinger Position gauge */}
                <div className="detail-card bollinger-detail-card">
                  <h4>Bollinger Bands</h4>
                  <div className="detail-rows">
                    <div className="detail-row">
                      <span className="detail-label">Upper Band</span>
                      <span className="detail-value">{formatCurrency(entryConditions?.bollinger_upper)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Middle Band</span>
                      <span className="detail-value">{formatCurrency(entryConditions?.bollinger_middle)}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Lower Band</span>
                      <span className="detail-value">{formatCurrency(entryConditions?.bollinger_lower)}</span>
                    </div>
                  </div>
                </div>

                {/* Entry Information - If present, shows at the end */}
                {tradeGroup.bollingerEntryTime && (
                  <div className="detail-card">
                    <h4>Entry Information</h4>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <span className="detail-label">Entry Time</span>
                        <span className="detail-value">
                          {new Date(tradeGroup.bollingerEntryTime).toLocaleString()}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Time Elapsed</span>
                        <span className="detail-value">
                          {Math.floor((Date.now() - tradeGroup.bollingerEntryTime) / (1000 * 60))} minutes
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : tradeGroup.tradeGroupType !== 'BOLLINGER_REVERSION' && tradeGroup.tradeGroupType !== 'RSI_EXTREMES' && tradeGroup.tradeGroupType !== 'MACD_REVERSION' && tradeGroup.tradeGroupType !== 'VWAP_REVERSION' && tradeGroup.tradeGroupType !== 'PIVOT_POINT_TRADING' ? (
            /* Traditional Price Range Summary for laddered trade group types.
               Excludes single-position strategies (Bollinger/RSI/MACD/VWAP/Pivot Point)
               which have no buy/sell ladder range to visualize. */
            <div className="price-ranges-section">
              <h3 className="section-title">Price Ranges</h3>
              <div className="price-ranges">
                <div className="price-range-card">
                  <h4>Buy Range</h4>
                  <div className="range-values">
                    <div className="range-value">
                      <span className="range-label">Range %</span>
                      <span className="range-amount">{formatPercent(tradeGroup.buyPriceRangePct)}</span>
                    </div>
                    <div className="range-value">
                      <span className="range-label">
                        {calculatePositionInfo(tradeGroup.minBuyPrice, tradeGroup.maxBuyPrice, tradeGroup.currentPrice).label}
                      </span>
                      <span className={`range-amount position-${calculatePositionInfo(tradeGroup.minBuyPrice, tradeGroup.maxBuyPrice, tradeGroup.currentPrice).status}`}>
                        {calculatePositionInfo(tradeGroup.minBuyPrice, tradeGroup.maxBuyPrice, tradeGroup.currentPrice).value}
                      </span>
                    </div>
                  </div>
                  {renderPriceRuler(tradeGroup.minBuyPrice, tradeGroup.maxBuyPrice, tradeGroup.currentPrice, 'buy')}
                </div>

                <div className="price-range-card">
                  <h4>Sell Range</h4>
                  <div className="range-values">
                    <div className="range-value">
                      <span className="range-label">Range %</span>
                        <span className="range-amount">{formatPercent(tradeGroup.sellPriceRangePct)}</span>
                    </div>
                    <div className="range-value">
                      <span className="range-label">
                        {calculatePositionInfo(tradeGroup.minSellPrice, tradeGroup.maxSellPrice, tradeGroup.currentPrice).label}
                      </span>
                      <span className={`range-amount position-${calculatePositionInfo(tradeGroup.minSellPrice, tradeGroup.maxSellPrice, tradeGroup.currentPrice).status}`}>
                        {calculatePositionInfo(tradeGroup.minSellPrice, tradeGroup.maxSellPrice, tradeGroup.currentPrice).value}
                      </span>
                    </div>
                  </div>
                  {renderPriceRuler(tradeGroup.minSellPrice, tradeGroup.maxSellPrice, tradeGroup.currentPrice, 'sell')}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </StandaloneSection>

      {/* Bollinger Reversion Strategy Section - Only shown for BOLLINGER_REVERSION type */}
      {tradeGroup.tradeGroupType === 'BOLLINGER_REVERSION' && (
        <StandaloneSection title="Bollinger Mean Reversion Strategy">
          <div className="card-body">
            {/* Cumulative Performance Metrics */}
            <div className="bollinger-performance">
              <h4 className="subsection-title">Cumulative Performance</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cycles Started</div>
                    <div className="metric-value">
                      {tradeGroup.bollingerCycleCount || 0}
                    </div>
                  </div>
                  <div className="metric-footer">
                    Total entry/exit cycles
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Win Count</div>
                    <div className="metric-value profit-positive">{tradeGroup.bollingerWinCount || 0}</div>
                  </div>
                  <div className="metric-footer">
                    Win Rate: {tradeGroup.bollingerWinRate != null
                      ? (tradeGroup.bollingerWinRate * 100).toFixed(1)
                      : '0.0'}%
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Loss Count</div>
                    <div className="metric-value profit-negative">{tradeGroup.bollingerLossCount || 0}</div>
                  </div>
                  <div className="metric-footer">Stop-loss exits</div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cumulative P&L</div>
                    <div className={`metric-value ${getProfitClass(tradeGroup.bollingerCumulativePnl)}`}>
                      {formatCurrency(tradeGroup.bollingerCumulativePnl || 0)}
                    </div>
                  </div>
                  <div className="metric-footer">All-time profit/loss</div>
                </div>
              </div>
            </div>

            {/* Current Position Details - Only shown when in position */}
            {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && (
              <div className="bollinger-position">
                <h4 className="subsection-title">Current Position</h4>
                
                {/* Entry Details - Consistent card grid */}
                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Price</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.bollingerEntryPrice)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry RSI</span>
                    <span className="detail-value rsi-value">{tradeGroup.bollingerEntryRsi?.toFixed(2) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Market Regime</span>
                    <span className="detail-value">{tradeGroup.bollingerMarketRegime || 'UNKNOWN'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Time</span>
                    <span className="detail-value">
                      {tradeGroup.bollingerEntryTime ? new Date(tradeGroup.bollingerEntryTime).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                  {tradeGroup.state === 'PARTIAL_EXIT' && (
                    <div className="position-detail-card highlight">
                      <span className="detail-label">Position Status</span>
                      <span className="detail-value">70% Exited</span>
                    </div>
                  )}
                </div>

                {/* Price Targets and Bollinger Bands Visualization */}
                <div className="bollinger-bands-viz">
                  <h5 className="viz-title">Bollinger Bands & Targets</h5>
                  <div className="bands-ruler">
                    <div className="band-track">
                      {/* Stop Loss */}
                      {tradeGroup.bollingerStopLoss && (
                        <div 
                          className="band-line stop-loss" 
                          style={{ 
                            bottom: `${calculateBandPosition(
                              tradeGroup.bollingerStopLoss,
                              tradeGroup.bollingerStopLoss,
                              tradeGroup.bollingerUpperBand
                            )}%` 
                          }}
                          title={`Stop Loss: ${formatCurrency(tradeGroup.bollingerStopLoss)}`}
                        >
                          <span className="band-label">Stop Loss</span>
                          <span className="band-price">{formatCurrency(tradeGroup.bollingerStopLoss)}</span>
                        </div>
                      )}
                      
                      {/* Lower Band (Entry) */}
                      {tradeGroup.bollingerLowerBand && (
                        <div 
                          className="band-line lower-band" 
                          style={{ 
                            bottom: `${calculateBandPosition(
                              tradeGroup.bollingerLowerBand,
                              tradeGroup.bollingerStopLoss || tradeGroup.bollingerLowerBand,
                              tradeGroup.bollingerUpperBand
                            )}%` 
                          }}
                          title={`Lower Band: ${formatCurrency(tradeGroup.bollingerLowerBand)}`}
                        >
                          <span className="band-label">Lower Band (Entry)</span>
                          <span className="band-price">{formatCurrency(tradeGroup.bollingerLowerBand)}</span>
                        </div>
                      )}

                      {/* Middle Band (Primary Target) */}
                      {tradeGroup.bollingerMiddleBand && (
                        <div 
                          className="band-line middle-band" 
                          style={{ 
                            bottom: `${calculateBandPosition(
                              tradeGroup.bollingerMiddleBand,
                              tradeGroup.bollingerStopLoss || tradeGroup.bollingerLowerBand,
                              tradeGroup.bollingerUpperBand
                            )}%` 
                          }}
                          title={`Middle Band (Primary Target): ${formatCurrency(tradeGroup.bollingerMiddleBand)}`}
                        >
                          <span className="band-label">Middle Band (70% Exit)</span>
                          <span className="band-price">{formatCurrency(tradeGroup.bollingerMiddleBand)}</span>
                        </div>
                      )}

                      {/* Upper Band (Extended Target) */}
                      {tradeGroup.bollingerUpperBand && (
                        <div 
                          className="band-line upper-band" 
                          style={{ 
                            bottom: `${calculateBandPosition(
                              tradeGroup.bollingerUpperBand,
                              tradeGroup.bollingerStopLoss || tradeGroup.bollingerLowerBand,
                              tradeGroup.bollingerUpperBand
                            )}%` 
                          }}
                          title={`Upper Band (Extended Target): ${formatCurrency(tradeGroup.bollingerUpperBand)}`}
                        >
                          <span className="band-label">Upper Band (30% Exit)</span>
                          <span className="band-price">{formatCurrency(tradeGroup.bollingerUpperBand)}</span>
                        </div>
                      )}

                      {/* Current Price Marker - only show when we have valid band data */}
                      {tradeGroup.bollingerUpperBand && tradeGroup.bollingerStopLoss && (
                        <div
                          className="current-price-indicator"
                          style={{
                            bottom: `${calculateBandPosition(
                              tradeGroup.currentPrice,
                              tradeGroup.bollingerStopLoss,
                              tradeGroup.bollingerUpperBand
                            )}%`
                          }}
                          title={`Current Price: ${formatCurrency(tradeGroup.currentPrice)}`}
                        >
                          <div className="price-dot"></div>
                          <span className="price-tag">NOW: {formatCurrency(tradeGroup.currentPrice)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Unrealized P&L */}
                <div className="bollinger-pnl-summary">
                  <div className="pnl-row">
                    <span className="pnl-label">Unrealized P&L:</span>
                    <span className={`pnl-value ${getProfitClass(tradeGroup.openSellUnrealizedProfit)}`}>
                      {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
                    </span>
                  </div>
                  <div className="pnl-row">
                    <span className="pnl-label">Time in Position:</span>
                    <span className="pnl-value">
                      {tradeGroup.bollingerEntryTime ? 
                        `${Math.floor((Date.now() - tradeGroup.bollingerEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.bollingerEntryTime) % 3600000) / 60000)}m` 
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Bollinger Exit Conditions Indicator */}
                <BollingerExitConditionsBar
                  exitConditions={{
                    currentPrice: tradeGroup.currentPrice,
                    stopLoss: tradeGroup.bollingerStopLoss,
                    middleBand: tradeGroup.bollingerMiddleBand,
                    upperBand: tradeGroup.bollingerUpperBand,
                    isPartialExit: tradeGroup.state === 'PARTIAL_EXIT',
                  }}
                  isLoading={false}
                />
              </div>
            )}

            {/* Watching State - No current position */}
            {tradeGroup.state === 'WATCHING' && (
              <div className="bollinger-watching">
                <div className="watching-message">
                  <h4>⏳ Waiting for Entry Signal</h4>
                  <p>Monitoring for lower Bollinger Band touch in RANGING market with RSI &lt; 35</p>
                  {tradeGroup.bollingerCycleCount && tradeGroup.bollingerCycleCount > 0 && (
                    <p className="last-cycle-info">
                      Last cycle completed. Ready for next entry signal.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Paused State */}
            {tradeGroup.state === 'PAUSED' && (
              <div className="bollinger-paused">
                <div className="paused-message">
                  <h4>⏸️ Strategy Paused</h4>
                  <p>Trading paused due to consecutive losses or daily loss limit</p>
                </div>
              </div>
            )}

            {/* Operator-Paused State - REQ-013: distinct from automated PAUSED.
                Will NOT auto-resume; must be resumed manually by an operator. */}
            {tradeGroup.state === 'OPERATOR_PAUSED' && (
              <div className="bollinger-paused">
                <div className="paused-message">
                  <h4>⏸️ Operator-Paused</h4>
                  <p>Manually paused by an operator. This group opens no new positions and will not auto-resume — it must be resumed manually.</p>
                </div>
              </div>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* RSI Extremes Strategy Section - Only shown for RSI_EXTREMES type */}
      {tradeGroup.tradeGroupType === 'RSI_EXTREMES' && (
        <StandaloneSection title="RSI Extremes Mean Reversion Strategy">
          <div className="card-body">
            {/* Technical Indicators Dashboard - Only shown when WATCHING for entry */}
            {tradeGroup.state === 'WATCHING' && (
              <div className="rsi-indicators-dashboard">
                <h3 className="section-title">Technical Indicators & Entry Conditions</h3>
                
                {/* Gauges Row - Ordered: RSI → Market Regime → Volatility */}
                <div className="gauges-row">
                  {/* RSI Gauge - Momentum indicator (leftmost) */}
                  <div className="gauge-container" title={`RSI Value: ${rsiEntryConditions?.rsi?.toFixed(2) ?? 'N/A'}`}>
                    <h4 className="gauge-title">RSI</h4>
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                        padding: 0.02,
                        subArcs: [
                          { limit: 30, color: '#10b981' },
                          { limit: 70, color: '#f59e0b' },
                          { limit: 100, color: '#ef4444' }
                        ]
                      }}
                      pointer={{
                        type: 'blob',
                        animationDelay: 0,
                        elastic: true,
                        color: '#ffffff'
                      }}
                      value={rsiEntryConditions?.rsi ?? 50}
                      minValue={0}
                      maxValue={100}
                      labels={{
                        valueLabel: {
                          formatTextValue: (value: number) => value.toFixed(1),
                          style: { fontSize: '2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                        },
                        tickLabels: {
                          type: 'inner',
                          ticks: [
                            { value: 30 },
                            { value: 70 }
                          ],
                          defaultTickValueConfig: {
                            style: { fontSize: '0.75rem', fill: '#94a3b8' },
                            formatTextValue: (value: number) => value.toString()
                          }
                        }
                      }}
                    />
                    <p className="gauge-description">
                      {rsiEntryConditions?.entry_conditions?.is_rsi_oversold
                        ? '✓ Oversold - Entry signal'
                        : (rsiEntryConditions?.rsi ?? 50) > 70
                        ? '⚠ Overbought'
                        : 'Neutral'}
                    </p>
                  </div>

                  {/* Market Regime Gauge - Sets overall context (center) */}
                  <div className="gauge-container">
                    <h4 className="gauge-title">Market Regime</h4>
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                        padding: 0.02,
                        subArcs: [
                          { limit: 40, color: '#10b981' },  // Green: RANGING (16) and REVERSAL_POTENTIAL (28)
                          { limit: 70, color: '#f59e0b' },  // Yellow: TRENDING (50)
                          { limit: 100, color: '#ef4444' }   // Red: VOLATILE (84)
                        ]
                      }}
                      pointer={{
                        type: 'blob',
                        animationDelay: 0,
                        elastic: true,
                        color: '#ffffff'
                      }}
                      value={
                        rsiEntryConditions?.market_regime === 'RANGING' ? 16 :
                        rsiEntryConditions?.market_regime === 'REVERSAL_POTENTIAL' ? 28 :
                        rsiEntryConditions?.market_regime === 'TRENDING' ||
                        rsiEntryConditions?.market_regime === 'TRENDING_UP' ||
                        rsiEntryConditions?.market_regime === 'TRENDING_DOWN' ? 50 :
                        rsiEntryConditions?.market_regime === 'VOLATILE' ? 84 : 50
                      }
                      minValue={0}
                      maxValue={100}
                      labels={{
                        valueLabel: {
                          formatTextValue: () => rsiEntryConditions?.market_regime || 'UNKNOWN',
                          style: { fontSize: '1.2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                        },
                        tickLabels: {
                          type: 'inner',
                          ticks: [
                            { value: 16, valueConfig: { formatTextValue: () => 'RANGING' } },
                            { value: 28, valueConfig: { formatTextValue: () => 'REVERSAL' } },
                            { value: 50, valueConfig: { formatTextValue: () => 'TRENDING' } },
                            { value: 84, valueConfig: { formatTextValue: () => 'VOLATILE' } }
                          ],
                          defaultTickValueConfig: {
                            style: { fontSize: '0.6rem', fill: '#94a3b8' }
                          }
                        }
                      }}
                    />
                    <p className="gauge-description">
                      {rsiEntryConditions?.entry_conditions?.is_ranging_market
                        ? `✓ ${rsiEntryConditions?.market_regime === 'REVERSAL_POTENTIAL' ? 'Reversal setup' : 'Ideal for mean reversion'}`
                        : rsiEntryConditions?.market_regime === 'VOLATILE'
                        ? '✗ Avoid entry'
                        : '⚠ Use caution'}
                    </p>
                  </div>

                  {/* Volatility Gauge - Risk indicator (rightmost) */}
                  <div className="gauge-container">
                    <h4 className="gauge-title">Volatility</h4>
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                        padding: 0.02,
                        subArcs: [
                          { limit: 33, color: '#10b981' },
                          { limit: 67, color: '#f59e0b' },
                          { limit: 100, color: '#ef4444' }
                        ]
                      }}
                      pointer={{
                        type: 'blob',
                        animationDelay: 0,
                        elastic: true,
                        color: '#ffffff'
                      }}
                      value={
                        rsiEntryConditions?.volatility_state === 'LOW' ? 16 :
                        rsiEntryConditions?.volatility_state === 'MEDIUM' ? 50 :
                        rsiEntryConditions?.volatility_state === 'HIGH' || rsiEntryConditions?.volatility_state === 'EXTREME' ? 84 : 50
                      }
                      minValue={0}
                      maxValue={100}
                      labels={{
                        valueLabel: {
                          formatTextValue: () => rsiEntryConditions?.volatility_state || 'UNKNOWN',
                          style: { fontSize: '1.2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                        },
                        tickLabels: {
                          type: 'inner',
                          ticks: [
                            { value: 16, valueConfig: { formatTextValue: () => 'LOW' } },
                            { value: 50, valueConfig: { formatTextValue: () => 'MEDIUM' } },
                            { value: 84, valueConfig: { formatTextValue: () => 'HIGH' } }
                          ],
                          defaultTickValueConfig: {
                            style: { fontSize: '0.65rem', fill: '#94a3b8' }
                          }
                        }
                      }}
                    />
                    <p className="gauge-description">
                      {rsiEntryConditions?.entry_conditions?.is_volatility_acceptable
                        ? '✓ Ideal conditions'
                        : rsiEntryConditions?.volatility_state === 'HIGH' || rsiEntryConditions?.volatility_state === 'EXTREME'
                        ? '✗ Too volatile'
                        : '⚠ Acceptable'}
                    </p>
                  </div>
                </div>

                {/* Entry Conditions Summary - All 6 conditions with labels */}
                <div className="entry-conditions-summary">
                  <h4>Entry Conditions ({rsiEntryConditions?.entry_conditions?.conditions_met_count ?? 0}/6 met)</h4>
                  <div className="conditions-grid">
                    {/* RSI condition */}
                    <div
                      className={`condition-item ${rsiEntryConditions?.entry_conditions?.is_rsi_oversold ? 'met' : 'not-met'}`}
                      title={`Current RSI: ${rsiEntryConditions?.rsi?.toFixed(2) ?? 'N/A'} | Threshold: <${rsiEntryConditions?.rsi_oversold_threshold ?? 30}`}
                    >
                      <span className="condition-icon">
                        {rsiEntryConditions?.entry_conditions?.is_rsi_oversold ? '✓' : '✗'}
                      </span>
                      <span className="condition-label">RSI Oversold (&lt;{rsiEntryConditions?.rsi_oversold_threshold ?? 30})</span>
                    </div>
                    {/* Volatility condition */}
                    <div
                      className={`condition-item ${rsiEntryConditions?.entry_conditions?.is_volatility_acceptable ? 'met' : 'not-met'}`}
                      title={`Current Volatility State: ${rsiEntryConditions?.volatility_state ?? 'N/A'} | Required: LOW or MEDIUM`}
                    >
                      <span className="condition-icon">
                        {rsiEntryConditions?.entry_conditions?.is_volatility_acceptable ? '✓' : '✗'}
                      </span>
                      <span className="condition-label">Volatility Acceptable (LOW or MEDIUM)</span>
                    </div>
                    {/* Market regime condition */}
                    <div
                      className={`condition-item ${rsiEntryConditions?.entry_conditions?.is_ranging_market ? 'met' : 'not-met'}`}
                      title={`Current Market Regime: ${rsiEntryConditions?.market_regime ?? 'N/A'} | Required: RANGING or REVERSAL_POTENTIAL`}
                    >
                      <span className="condition-icon">
                        {rsiEntryConditions?.entry_conditions?.is_ranging_market ? '✓' : '✗'}
                      </span>
                      <span className="condition-label">Mean Reversion Market</span>
                    </div>
                    {/* Trend condition */}
                    <div
                      className={`condition-item ${rsiEntryConditions?.entry_conditions?.is_weak_trend ? 'met' : 'not-met'}`}
                      title={`Current Trend Strength: ${rsiEntryConditions?.trend_strength !== null && rsiEntryConditions?.trend_strength !== undefined ? Math.abs(rsiEntryConditions.trend_strength).toFixed(3) : 'N/A'} | Threshold: <0.7`}
                    >
                      <span className="condition-icon">
                        {rsiEntryConditions?.entry_conditions?.is_weak_trend ? '✓' : '✗'}
                      </span>
                      <span className="condition-label">Moderate Trend (&lt;0.7 strength)</span>
                    </div>
                    {/* Volatility percentile condition */}
                    <div
                      className={`condition-item ${rsiEntryConditions?.entry_conditions?.is_volatility_percentile_ok ? 'met' : 'not-met'}`}
                      title={`Current Volatility Percentile: ${rsiEntryConditions?.volatility_percentile?.toFixed(1) ?? 'N/A'} | Threshold: <70`}
                    >
                      <span className="condition-icon">
                        {rsiEntryConditions?.entry_conditions?.is_volatility_percentile_ok ? '✓' : '✗'}
                      </span>
                      <span className="condition-label">Volatility Percentile (&lt;70th)</span>
                    </div>
                    {/* Volume condition */}
                    <div
                      className={`condition-item ${rsiEntryConditions?.entry_conditions?.is_volume_normal ? 'met' : 'not-met'}`}
                      title={`Current Relative Volume: ${rsiEntryConditions?.relative_volume?.toFixed(2) ?? 'N/A'}x | Threshold: <2.0x average`}
                    >
                      <span className="condition-icon">
                        {rsiEntryConditions?.entry_conditions?.is_volume_normal ? '✓' : '✗'}
                      </span>
                      <span className="condition-label">Normal Volume (&lt;2.0x average)</span>
                    </div>
                  </div>
                </div>

                {/* Additional Details Grid */}
                <div className="bollinger-details-grid">
                  {/* Trading Parameters */}
                  <div className="detail-card">
                    <h5 className="detail-card-title">Trading Parameters</h5>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <span className="detail-label">Mean Reversion Period:</span>
                        <span className="detail-value">{tradeGroup.rsiStrategyMetricPeriod || 'MEDIUM'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">RSI Overbought Threshold:</span>
                        <span className="detail-value">{tradeGroup.rsiOverboughtThreshold || 70}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">RSI Oversold Threshold:</span>
                        <span className="detail-value">{tradeGroup.rsiOversoldThreshold || 30}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Cumulative Performance Metrics */}
            <div className="rsi-performance">
              <h4 className="subsection-title">Cumulative Performance</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cycles Started</div>
                    <div className="metric-value">
                      {tradeGroup.rsiCycleCount || 0}
                    </div>
                  </div>
                  <div className="metric-footer">
                    Total entry/exit cycles
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Win Count</div>
                    <div className="metric-value profit-positive">{tradeGroup.rsiWinCount || 0}</div>
                  </div>
                  <div className="metric-footer">
                    Win Rate: {tradeGroup.rsiWinRate != null
                      ? (tradeGroup.rsiWinRate * 100).toFixed(1)
                      : '0.0'}%
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Loss Count</div>
                    <div className="metric-value profit-negative">{tradeGroup.rsiLossCount || 0}</div>
                  </div>
                  <div className="metric-footer">Stop-loss exits</div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cumulative P&L</div>
                    <div className={`metric-value ${getProfitClass(tradeGroup.rsiCumulativePnl)}`}>
                      {formatCurrency(tradeGroup.rsiCumulativePnl || 0)}
                    </div>
                  </div>
                  <div className="metric-footer">All-time profit/loss</div>
                </div>
              </div>
            </div>

            {/* Current Position Details - Only shown when in position */}
            {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && (
              <div className="rsi-position">
                <h4 className="subsection-title">Current Position</h4>
                
                {/* Entry Details - Consistent card grid */}
                <div className="position-details-grid">
                  {/*<div className="position-detail-card">*/}
                  {/*  <span className="detail-label">Entry Price</span>*/}
                  {/*  <span className="detail-value">{formatCurrency(tradeGroup.rsiEntryPrice)}</span>*/}
                  {/*</div>*/}
                  {/*<div className="position-detail-card">*/}
                  {/*  <span className="detail-label">Entry RSI</span>*/}
                  {/*  <span className="detail-value rsi-value">{tradeGroup.rsiEntryRsi?.toFixed(2) || 'N/A'}</span>*/}
                  {/*</div>*/}
                    <div className="position-detail-card">
                        <span className="detail-label">Entry Time</span>
                        <span className="detail-value">
                      {tradeGroup.rsiEntryTime ? new Date(tradeGroup.rsiEntryTime).toLocaleString() : 'N/A'}
                    </span>
                    </div>
                  <div className="position-detail-card">
                    <span className="detail-label">
                      Market Regime
                      {!tradeGroup.rsiMarketRegime && rsiEntryConditions?.market_regime && (
                        <span style={{ fontSize: '0.7em', opacity: 0.6, marginLeft: '4px' }}>(current)</span>
                      )}
                    </span>
                    <span className="detail-value">
                      {tradeGroup.rsiMarketRegime ||
                       (rsiEntryConditions?.market_regime) ||
                       'UNKNOWN'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">
                      Volatility State
                      {!tradeGroup.rsiVolatilityState && rsiEntryConditions?.volatility_state && (
                        <span style={{ fontSize: '0.7em', opacity: 0.6, marginLeft: '4px' }}>(current)</span>
                      )}
                    </span>
                    <span className="detail-value">
                      {tradeGroup.rsiVolatilityState ||
                       (rsiEntryConditions?.volatility_state) ||
                       'UNKNOWN'}
                    </span>
                  </div>
                  {tradeGroup.state === 'PARTIAL_EXIT' && (
                    <div className="position-detail-card highlight">
                      <span className="detail-label">Position Status</span>
                      <span className="detail-value">70% Exited</span>
                    </div>
                  )}
                </div>

                {/* Cycle status message - full width below grid */}
                {tradeGroup.rsiStateChangeReason && (
                  <div className="position-status-message">
                    <span className="status-label">Cycle Status</span>
                    <span className="status-text">{tradeGroup.rsiStateChangeReason}</span>
                  </div>
                )}

                {/* RSI Gauge Visualization */}
                <div className="rsi-gauge-section">
                  <h5 className="viz-title">RSI Level</h5>
                  {rsiEntryConditions?.rsi != null ? (
                    <div className="rsi-gauge-container">
                      <GaugeComponent
                        type="semicircle"
                        arc={{
                          width: 0.2,
                          padding: 0.005,
                          cornerRadius: 1,
                          subArcs: [
                            {
                              limit: 30,
                              color: '#10b981',
                              showTick: true,
                              tooltip: {
                                text: 'Oversold Zone (< 30)'
                              }
                            },
                            {
                              limit: 70,
                              color: '#94a3b8',
                              showTick: true,
                              tooltip: {
                                text: 'Neutral Zone (30-70)'
                              }
                            },
                            {
                              limit: 100,
                              color: '#ef4444',
                              showTick: true,
                              tooltip: {
                                text: 'Overbought Zone (> 70)'
                              }
                            }
                          ]
                        }}
                        pointer={{
                          color: '#fb923c',
                          length: 0.80,
                          width: 15,
                        }}
                        labels={{
                          valueLabel: {
                            formatTextValue: (value: number) => value.toFixed(1),
                            style: {
                              fontSize: '35px',
                              fill: '#fff',
                              textShadow: 'black 1px 1px 0px, black 0px 0px 2.5em, black 0px 0px 0.2em'
                            }
                          },
                          tickLabels: {
                            type: 'inner',
                            ticks: [
                              { value: 30 },
                              { value: 50 },
                              { value: 70 }
                            ],
                            defaultTickValueConfig: {
                              style: { fontSize: 10, fill: '#cbd5e1' }
                            }
                          }
                        }}
                        value={rsiEntryConditions?.rsi ?? 50}
                        minValue={0}
                        maxValue={100}
                      />
                      <div className="gauge-info">
                        <div className="gauge-info-row">
                          <span>Entry RSI:</span>
                          <span className="rsi-oversold">{tradeGroup.rsiEntryRsi?.toFixed(1) || 'N/A'}</span>
                        </div>
                        <div className="gauge-info-row">
                          <span>Current RSI:</span>
                          <span className="rsi-current">{rsiEntryConditions?.rsi?.toFixed(1) ?? 'N/A'}</span>
                        </div>
                        <div className="gauge-info-row">
                          <span>Overbought Target:</span>
                          <span className="rsi-overbought">{tradeGroup.rsiOverboughtThreshold || 70}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rsi-no-data">
                      {isRsiLoading ? 'Loading RSI data...' : 'Current RSI data not available'}
                    </div>
                  )}
                </div>

                {/* Position Metrics Summary */}
                <div className="rsi-pnl-summary">
                    { tradeGroup.rsiEntryPrice && (
                        <div>
                        <div className="pnl-row">
                            <span className="pnl-label">Unrealized P&L:</span>
                            <span className={`pnl-value ${getProfitClass(tradeGroup.openSellUnrealizedProfit)}`}>
                      {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
                            </span>
                        </div>
                      <div className="pnl-row">
                        <span className="pnl-label">Change from Entry:</span>
                        <span className={`pnl-value ${tradeGroup.currentPrice >= tradeGroup.rsiEntryPrice ? 'positive' : 'negative'}`}>
                            {tradeGroup.currentPrice >= tradeGroup.rsiEntryPrice ? '+' : ''}
                            {(((tradeGroup.currentPrice - tradeGroup.rsiEntryPrice) / tradeGroup.rsiEntryPrice) * 100).toFixed(2)}%
                        </span>
                      </div>
                        </div>
                    )}
                  <div className="pnl-row">
                    <span className="pnl-label">Time in Position:</span>
                    <span className="pnl-value">
                      {tradeGroup.rsiEntryTime ? 
                        `${Math.floor((Date.now() - tradeGroup.rsiEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.rsiEntryTime) % 3600000) / 60000)}m` 
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* RSI Exit Conditions Indicator */}
                <RsiExtremesExitConditionsBar
                  exitConditions={{
                    currentPrice: tradeGroup.currentPrice,
                    currentRsi: rsiEntryConditions?.rsi ?? undefined,
                    stopLoss: tradeGroup.rsiStopLoss,
                    rsiOverboughtThreshold: tradeGroup.rsiOverboughtThreshold || 70,
                    profitTarget: tradeGroup.rsiProfitTarget,
                    trailingStopActivation: tradeGroup.rsiTrailingStopActivationPrice,
                    trailingStopPrice: tradeGroup.rsiTrailingStopDistance ? tradeGroup.currentPrice * (1 - tradeGroup.rsiTrailingStopDistance / 100) : undefined,
                    timeRemainingHours: tradeGroup.rsiEntryTime ? 72 - (Date.now() - tradeGroup.rsiEntryTime) / 3600000 : undefined,
                    isPartialExit: tradeGroup.state === 'PARTIAL_EXIT',
                  }}
                  isLoading={isRsiLoading}
                />
              </div>
            )}

            {/* Watching State - No current position */}
            {tradeGroup.state === 'WATCHING' && tradeGroup.rsiCycleCount && tradeGroup.rsiCycleCount > 0 && (
              <div className="rsi-watching">
                <div className="watching-message">
                  <p className="last-cycle-info">
                    Last cycle completed. Ready for next entry signal.
                  </p>
                </div>
              </div>
            )}

            {/* Paused State */}
            {tradeGroup.state === 'PAUSED' && (
              <div className="rsi-paused">
                <div className="paused-message">
                  <h4>⏸️ Strategy Paused</h4>
                  <p>Trading paused due to consecutive losses or daily loss limit</p>
                </div>
              </div>
            )}

            {/* Operator-Paused State - REQ-013: distinct from automated PAUSED.
                Will NOT auto-resume; must be resumed manually by an operator. */}
            {tradeGroup.state === 'OPERATOR_PAUSED' && (
              <div className="rsi-paused">
                <div className="paused-message">
                  <h4>⏸️ Operator-Paused</h4>
                  <p>Manually paused by an operator. This group opens no new positions and will not auto-resume — it must be resumed manually.</p>
                </div>
              </div>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* MACD Reversion Strategy Section - Only shown for MACD_REVERSION type */}
      {tradeGroup.tradeGroupType === 'MACD_REVERSION' && (
        <StandaloneSection title="MACD Mean Reversion Strategy">
          <div className="card-body">
            {/* Technical Indicators Dashboard - Only shown when WATCHING for entry */}
            {tradeGroup.state === 'WATCHING' && (
              <div className="macd-indicators-dashboard">
                <h3 className="section-title">Technical Indicators & Entry Conditions</h3>

                {/* Gauges Row - Ordered: RSI → MACD Histogram → Market Regime */}
                <div className="gauges-row">
                  {/* RSI Gauge - Momentum indicator (leftmost) */}
                  <div className="gauge-container" title={`RSI Value: ${macdEntryConditions?.rsi?.toFixed(2) ?? 'N/A'}`}>
                    <h4 className="gauge-title">RSI</h4>
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                        padding: 0.02,
                        subArcs: [
                          { limit: 40, color: '#10b981' },
                          { limit: 70, color: '#f59e0b' },
                          { limit: 100, color: '#ef4444' }
                        ]
                      }}
                      pointer={{
                        type: 'blob',
                        animationDelay: 0,
                        elastic: true,
                        color: '#ffffff'
                      }}
                      value={macdEntryConditions?.rsi ?? 50}
                      minValue={0}
                      maxValue={100}
                      labels={{
                        valueLabel: {
                          formatTextValue: (value: number) => value.toFixed(1),
                          style: { fontSize: '2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                        },
                        tickLabels: {
                          type: 'inner',
                          ticks: [
                            { value: 40 },
                            { value: 70 }
                          ],
                          defaultTickValueConfig: {
                            style: { fontSize: '0.75rem', fill: '#94a3b8' },
                            formatTextValue: (value: number) => value.toString()
                          }
                        }
                      }}
                    />
                    <p className="gauge-description">
                      {macdEntryConditions?.entry_conditions?.is_rsi_moderately_oversold
                        ? '✓ Moderately Oversold'
                        : (macdEntryConditions?.rsi ?? 50) > 70
                        ? '⚠ Overbought'
                        : 'Neutral'}
                    </p>
                  </div>

                  {/* MACD Histogram Gauge - Momentum exhaustion indicator (center) */}
                  <div className="gauge-container" title={`Histogram: ${macdEntryConditions?.histogram?.toFixed(6) ?? 'N/A'}`}>
                    <h4 className="gauge-title">MACD Histogram</h4>
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                        padding: 0.02,
                        subArcs: [
                          { limit: 33, color: '#10b981' },
                          { limit: 67, color: '#f59e0b' },
                          { limit: 100, color: '#ef4444' }
                        ]
                      }}
                      pointer={{
                        type: 'blob',
                        animationDelay: 0,
                        elastic: true,
                        color: '#ffffff'
                      }}
                      value={
                        macdEntryConditions?.entry_conditions?.is_histogram_recovering ? 16 :
                        macdEntryConditions?.entry_conditions?.is_macd_below_signal ? 50 : 84
                      }
                      minValue={0}
                      maxValue={100}
                      labels={{
                        valueLabel: {
                          formatTextValue: () =>
                            macdEntryConditions?.entry_conditions?.is_histogram_recovering
                              ? 'RECOVERING'
                              : macdEntryConditions?.entry_conditions?.is_macd_below_signal
                              ? 'BEARISH'
                              : 'BULLISH',
                          style: { fontSize: '1.2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                        },
                        tickLabels: {
                          type: 'inner',
                          ticks: [
                            { value: 16, valueConfig: { formatTextValue: () => 'RECOVERY' } },
                            { value: 50, valueConfig: { formatTextValue: () => 'BEARISH' } },
                            { value: 84, valueConfig: { formatTextValue: () => 'BULLISH' } }
                          ],
                          defaultTickValueConfig: {
                            style: { fontSize: '0.6rem', fill: '#94a3b8' }
                          }
                        }
                      }}
                    />
                    <p className="gauge-description">
                      {macdEntryConditions?.entry_conditions?.is_histogram_recovering
                        ? '✓ Histogram recovering - Entry signal'
                        : macdEntryConditions?.entry_conditions?.is_macd_below_signal
                        ? '⚠ MACD below signal, waiting for recovery'
                        : '✗ Not in entry zone'}
                    </p>
                  </div>

                  {/* Market Regime Gauge - Sets overall context (rightmost) */}
                  <div className="gauge-container">
                    <h4 className="gauge-title">Market Regime</h4>
                    <GaugeComponent
                      type="semicircle"
                      arc={{
                        colorArray: ['#10b981', '#f59e0b', '#ef4444'],
                        padding: 0.02,
                        subArcs: [
                          { limit: 40, color: '#10b981' },
                          { limit: 70, color: '#f59e0b' },
                          { limit: 100, color: '#ef4444' }
                        ]
                      }}
                      pointer={{
                        type: 'blob',
                        animationDelay: 0,
                        elastic: true,
                        color: '#ffffff'
                      }}
                      value={
                        macdEntryConditions?.market_regime === 'RANGING' ? 16 :
                        macdEntryConditions?.market_regime === 'REVERSAL_POTENTIAL' ? 28 :
                        macdEntryConditions?.market_regime === 'TRENDING' ||
                        macdEntryConditions?.market_regime === 'TRENDING_UP' ||
                        macdEntryConditions?.market_regime === 'TRENDING_DOWN' ? 50 :
                        macdEntryConditions?.market_regime === 'VOLATILE' ? 84 : 50
                      }
                      minValue={0}
                      maxValue={100}
                      labels={{
                        valueLabel: {
                          formatTextValue: () => macdEntryConditions?.market_regime || 'UNKNOWN',
                          style: { fontSize: '1.2rem', fill: '#f1f5f9', fontWeight: 'bold' }
                        },
                        tickLabels: {
                          type: 'inner',
                          ticks: [
                            { value: 16, valueConfig: { formatTextValue: () => 'RANGING' } },
                            { value: 28, valueConfig: { formatTextValue: () => 'REVERSAL' } },
                            { value: 50, valueConfig: { formatTextValue: () => 'TRENDING' } },
                            { value: 84, valueConfig: { formatTextValue: () => 'VOLATILE' } }
                          ],
                          defaultTickValueConfig: {
                            style: { fontSize: '0.6rem', fill: '#94a3b8' }
                          }
                        }
                      }}
                    />
                    <p className="gauge-description">
                      {macdEntryConditions?.entry_conditions?.is_regime_favorable
                        ? `✓ ${macdEntryConditions?.market_regime === 'REVERSAL_POTENTIAL' ? 'Reversal setup' : 'Ideal for mean reversion'}`
                        : macdEntryConditions?.market_regime === 'VOLATILE'
                        ? '✗ Avoid entry'
                        : '⚠ Use caution'}
                    </p>
                  </div>
                </div>

                {/* Entry Conditions Summary - All 6 conditions */}
                <div className="entry-conditions-summary">
                  <h4>Entry Conditions ({macdEntryConditions?.entry_conditions?.conditions_met_count ?? 0}/6 met)</h4>
                  <div className="conditions-grid">
                    <div
                      className={`condition-item ${macdEntryConditions?.entry_conditions?.is_histogram_recovering ? 'met' : 'not-met'}`}
                      title={`Histogram: ${macdEntryConditions?.histogram?.toFixed(6) ?? 'N/A'} | Previous: ${macdEntryConditions?.previous_histogram?.toFixed(6) ?? 'N/A'}`}
                    >
                      <span className="condition-icon">{macdEntryConditions?.entry_conditions?.is_histogram_recovering ? '✓' : '✗'}</span>
                      <span className="condition-label">Histogram Recovering</span>
                    </div>
                    <div
                      className={`condition-item ${macdEntryConditions?.entry_conditions?.is_macd_below_signal ? 'met' : 'not-met'}`}
                      title={`MACD: ${macdEntryConditions?.macd_line?.toFixed(6) ?? 'N/A'} | Signal: ${macdEntryConditions?.signal_line?.toFixed(6) ?? 'N/A'}`}
                    >
                      <span className="condition-icon">{macdEntryConditions?.entry_conditions?.is_macd_below_signal ? '✓' : '✗'}</span>
                      <span className="condition-label">MACD Below Signal</span>
                    </div>
                    <div
                      className={`condition-item ${macdEntryConditions?.entry_conditions?.is_rsi_moderately_oversold ? 'met' : 'not-met'}`}
                      title={`Current RSI: ${macdEntryConditions?.rsi?.toFixed(2) ?? 'N/A'} | Threshold: <40`}
                    >
                      <span className="condition-icon">{macdEntryConditions?.entry_conditions?.is_rsi_moderately_oversold ? '✓' : '✗'}</span>
                      <span className="condition-label">RSI Moderately Oversold (&lt;40)</span>
                    </div>
                    <div
                      className={`condition-item ${macdEntryConditions?.entry_conditions?.is_regime_favorable ? 'met' : 'not-met'}`}
                      title={`Current Regime: ${macdEntryConditions?.market_regime ?? 'N/A'} | Required: RANGING or REVERSAL_POTENTIAL`}
                    >
                      <span className="condition-icon">{macdEntryConditions?.entry_conditions?.is_regime_favorable ? '✓' : '✗'}</span>
                      <span className="condition-label">Regime Favorable</span>
                    </div>
                    <div
                      className={`condition-item ${macdEntryConditions?.entry_conditions?.is_volatility_acceptable ? 'met' : 'not-met'}`}
                      title={`Volatility: ${macdEntryConditions?.volatility_state ?? 'N/A'} (${macdEntryConditions?.volatility_percentile?.toFixed(1) ?? 'N/A'}th pct) | Required: LOW or MEDIUM`}
                    >
                      <span className="condition-icon">{macdEntryConditions?.entry_conditions?.is_volatility_acceptable ? '✓' : '✗'}</span>
                      <span className="condition-label">Volatility Acceptable</span>
                    </div>
                    <div
                      className={`condition-item ${macdEntryConditions?.entry_conditions?.is_above_pivot_support ? 'met' : 'not-met'}`}
                      title={`S2 Pivot: ${macdEntryConditions?.s2_pivot_level != null ? formatCurrency(macdEntryConditions.s2_pivot_level) : 'N/A'} | Price must be above`}
                    >
                      <span className="condition-icon">{macdEntryConditions?.entry_conditions?.is_above_pivot_support ? '✓' : '✗'}</span>
                      <span className="condition-label">Above S2 Pivot</span>
                    </div>
                  </div>
                </div>

                {/* Additional Details Grid */}
                <div className="bollinger-details-grid">
                  <div className="detail-card">
                    <h5 className="detail-card-title">MACD Values</h5>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <span className="detail-label">MACD Line</span>
                        <span className="detail-value">{macdEntryConditions?.macd_line?.toFixed(6) ?? 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Signal Line</span>
                        <span className="detail-value">{macdEntryConditions?.signal_line?.toFixed(6) ?? 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Histogram</span>
                        <span className="detail-value">{macdEntryConditions?.histogram?.toFixed(6) ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="detail-card">
                    <h5 className="detail-card-title">Trading Parameters</h5>
                    <div className="detail-rows">
                      <div className="detail-row">
                        <span className="detail-label">Mean Reversion Period</span>
                        <span className="detail-value">{tradeGroup.macdStrategyMetricPeriod || 'MEDIUM'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Granularity</span>
                        <span className="detail-value">{macdEntryConditions?.granularity_mins ? `${macdEntryConditions.granularity_mins} min` : 'N/A'}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Trend Hostility</span>
                        <span className="detail-value">{macdEntryConditions?.trend_hostility?.toFixed(2) ?? 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  {macdEntryConditions?.s2_pivot_level && (
                    <div className="detail-card">
                      <h5 className="detail-card-title">S2 Pivot Level</h5>
                      <div className="detail-rows">
                        <div className="detail-row">
                          <span className="detail-label">Support Level</span>
                          <span className="detail-value">{formatCurrency(macdEntryConditions.s2_pivot_level)}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Pivot Timeframe</span>
                          <span className="detail-value">{macdEntryConditions.pivot_timeframe}</span>
                        </div>
                        <div className="detail-row">
                          <span className="detail-label">Distance</span>
                          <span className="detail-value">
                            {macdEntryConditions.s2_pivot_level && macdEntryConditions.current_price
                              ? `${((Math.abs(macdEntryConditions.current_price - macdEntryConditions.s2_pivot_level) / macdEntryConditions.s2_pivot_level) * 100).toFixed(2)}%`
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Cumulative Performance Metrics */}
            <div className="macd-performance">
              <h4 className="subsection-title">Cumulative Performance</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cycles Started</div>
                    <div className="metric-value">
                      {tradeGroup.macdCycleCount || 0}
                    </div>
                  </div>
                  <div className="metric-footer">Total entry/exit cycles</div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Win Count</div>
                    <div className="metric-value profit-positive">{tradeGroup.macdWinCount || 0}</div>
                  </div>
                  <div className="metric-footer">
                    Win Rate: {tradeGroup.macdWinRate != null
                      ? (tradeGroup.macdWinRate * 100).toFixed(1)
                      : '0.0'}%
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Loss Count</div>
                    <div className="metric-value profit-negative">{tradeGroup.macdLossCount || 0}</div>
                  </div>
                  <div className="metric-footer">Stop-loss exits</div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cumulative P&L</div>
                    <div className={`metric-value ${getProfitClass(tradeGroup.macdCumulativePnl)}`}>
                      {formatCurrency(tradeGroup.macdCumulativePnl || 0)}
                    </div>
                  </div>
                  <div className="metric-footer">All-time profit/loss</div>
                </div>
              </div>
            </div>

            {/* Current Position Details - Only shown when in position */}
            {tradeGroup.state === 'IN_POSITION' && (
              <div className="macd-position">
                <h4 className="subsection-title">Current Position</h4>

                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Price</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.macdEntryPrice)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry MACD</span>
                    <span className="detail-value">{tradeGroup.macdEntryMacdLine?.toFixed(6) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Signal</span>
                    <span className="detail-value">{tradeGroup.macdEntrySignalLine?.toFixed(6) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Time</span>
                    <span className="detail-value">
                      {tradeGroup.macdEntryTime ? new Date(tradeGroup.macdEntryTime).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {tradeGroup.macdStateChangeReason && (
                  <div className="position-status-message">
                    <span className="status-label">Cycle Status</span>
                    <span className="status-text">{tradeGroup.macdStateChangeReason}</span>
                  </div>
                )}

                {/* Position Metrics Summary */}
                <div className="macd-pnl-summary">
                  {tradeGroup.macdEntryPrice && (
                    <div>
                      <div className="pnl-row">
                        <span className="pnl-label">Unrealized P&L:</span>
                        <span className={`pnl-value ${getProfitClass(tradeGroup.openSellUnrealizedProfit)}`}>
                          {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
                        </span>
                      </div>
                      <div className="pnl-row">
                        <span className="pnl-label">Change from Entry:</span>
                        <span className={`pnl-value ${tradeGroup.currentPrice >= tradeGroup.macdEntryPrice ? 'positive' : 'negative'}`}>
                          {tradeGroup.currentPrice >= tradeGroup.macdEntryPrice ? '+' : ''}
                          {(((tradeGroup.currentPrice - tradeGroup.macdEntryPrice) / tradeGroup.macdEntryPrice) * 100).toFixed(2)}%
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="pnl-row">
                    <span className="pnl-label">Time in Position:</span>
                    <span className="pnl-value">
                      {tradeGroup.macdEntryTime ?
                        `${Math.floor((Date.now() - tradeGroup.macdEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.macdEntryTime) % 3600000) / 60000)}m`
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* MACD Exit Conditions Indicator */}
                <MacdExitConditionsBar
                  exitConditions={{
                    currentPrice: tradeGroup.currentPrice,
                    stopLoss: tradeGroup.macdStopLoss,
                    profitTarget: tradeGroup.macdProfitTarget,
                    entryPrice: tradeGroup.macdEntryPrice,
                  }}
                  isLoading={isMacdLoading}
                />
              </div>
            )}

            {/* Watching State - No current position */}
            {tradeGroup.state === 'WATCHING' && tradeGroup.macdCycleCount && tradeGroup.macdCycleCount > 0 && (
              <div className="macd-watching">
                <div className="watching-message">
                  <p className="last-cycle-info">
                    Last cycle completed. Ready for next entry signal.
                  </p>
                </div>
              </div>
            )}

            {/* Paused State */}
            {tradeGroup.state === 'PAUSED' && (
              <div className="macd-paused">
                <div className="paused-message">
                  <h4>⏸️ Strategy Paused</h4>
                  <p>Trading paused due to consecutive losses or daily loss limit</p>
                </div>
              </div>
            )}

            {/* Operator-Paused State - REQ-013: distinct from automated PAUSED.
                Will NOT auto-resume; must be resumed manually by an operator. */}
            {tradeGroup.state === 'OPERATOR_PAUSED' && (
              <div className="macd-paused">
                <div className="paused-message">
                  <h4>⏸️ Operator-Paused</h4>
                  <p>Manually paused by an operator. This group opens no new positions and will not auto-resume — it must be resumed manually.</p>
                </div>
              </div>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* MACD Momentum Strategy Section - Only shown for MACD_MOMENTUM type.
          Mirrors the MACD_REVERSION section structure (performance metrics,
          current-position panel, state-specific messages) relabeled for the
          crossover-momentum strategy. The MACD line/signal/histogram chart is
          rendered via the shared MacdPanel in the candlestick chart tab. */}
      {tradeGroup.tradeGroupType === 'MACD_MOMENTUM' && (
        <StandaloneSection title="MACD Momentum Strategy">
          <div className="card-body">
            {/* Cumulative Performance Metrics */}
            <div className="macd-performance">
              <h4 className="subsection-title">Cumulative Performance</h4>
              <div className="metrics-grid">
                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cycles Started</div>
                    <div className="metric-value">
                      {tradeGroup.macdMomentumCycleCount || 0}
                    </div>
                  </div>
                  <div className="metric-footer">Total entry/exit cycles</div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Win Count</div>
                    <div className="metric-value profit-positive">{tradeGroup.macdMomentumWinCount || 0}</div>
                  </div>
                  <div className="metric-footer">
                    Win Rate: {tradeGroup.macdMomentumWinRate != null
                      ? (tradeGroup.macdMomentumWinRate * 100).toFixed(1)
                      : '0.0'}%
                  </div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Loss Count</div>
                    <div className="metric-value profit-negative">{tradeGroup.macdMomentumLossCount || 0}</div>
                  </div>
                  <div className="metric-footer">Stop-loss exits</div>
                </div>

                <div className="metric-card">
                  <div className="metric-content">
                    <div className="metric-label">Cumulative P&L</div>
                    <div className={`metric-value ${getProfitClass(tradeGroup.macdMomentumCumulativePnl)}`}>
                      {formatCurrency(tradeGroup.macdMomentumCumulativePnl || 0)}
                    </div>
                  </div>
                  <div className="metric-footer">All-time profit/loss</div>
                </div>
              </div>
            </div>

            {/* Current Position Details - Only shown when in position */}
            {(tradeGroup.state === 'IN_POSITION' || tradeGroup.state === 'PARTIAL_EXIT') && (
              <div className="macd-position">
                <h4 className="subsection-title">Current Position</h4>

                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Price</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.macdMomentumEntryPrice)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry MACD</span>
                    <span className="detail-value">{tradeGroup.macdMomentumEntryMacdLine?.toFixed(6) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Signal</span>
                    <span className="detail-value">{tradeGroup.macdMomentumEntrySignalLine?.toFixed(6) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Histogram</span>
                    <span className="detail-value">{tradeGroup.macdMomentumEntryHistogram?.toFixed(6) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry RSI</span>
                    <span className="detail-value">{tradeGroup.macdMomentumEntryRsi?.toFixed(2) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Regime</span>
                    <span className="detail-value">{tradeGroup.macdMomentumEntryRegime || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Confidence Score</span>
                    <span className="detail-value">{tradeGroup.macdMomentumEntryConfidenceScore?.toFixed(0) || 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Time</span>
                    <span className="detail-value">
                      {tradeGroup.macdMomentumEntryTime ? new Date(tradeGroup.macdMomentumEntryTime).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {tradeGroup.macdMomentumStateChangeReason && (
                  <div className="position-status-message">
                    <span className="status-label">Cycle Status</span>
                    <span className="status-text">{tradeGroup.macdMomentumStateChangeReason}</span>
                  </div>
                )}

                {/* Position Metrics Summary */}
                <div className="macd-pnl-summary">
                  {tradeGroup.macdMomentumEntryPrice && (
                    <div>
                      <div className="pnl-row">
                        <span className="pnl-label">Unrealized P&L:</span>
                        <span className={`pnl-value ${getProfitClass(tradeGroup.openSellUnrealizedProfit)}`}>
                          {formatCurrency(tradeGroup.openSellUnrealizedProfit)}
                        </span>
                      </div>
                      <div className="pnl-row">
                        <span className="pnl-label">Change from Entry:</span>
                        <span className={`pnl-value ${tradeGroup.currentPrice >= tradeGroup.macdMomentumEntryPrice ? 'positive' : 'negative'}`}>
                          {tradeGroup.currentPrice >= tradeGroup.macdMomentumEntryPrice ? '+' : ''}
                          {(((tradeGroup.currentPrice - tradeGroup.macdMomentumEntryPrice) / tradeGroup.macdMomentumEntryPrice) * 100).toFixed(2)}%
                        </span>
                      </div>
                      {tradeGroup.macdMomentumTrailingStopEnabled && (
                        <div className="pnl-row">
                          <span className="pnl-label">Peak Price:</span>
                          <span className="pnl-value">{formatCurrency(tradeGroup.macdMomentumPeakPrice)}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pnl-row">
                    <span className="pnl-label">Time in Position:</span>
                    <span className="pnl-value">
                      {tradeGroup.macdMomentumEntryTime ?
                        `${Math.floor((Date.now() - tradeGroup.macdMomentumEntryTime) / 3600000)}h ${Math.floor(((Date.now() - tradeGroup.macdMomentumEntryTime) % 3600000) / 60000)}m`
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* MACD Exit Conditions Indicator (shared sibling component) */}
                <MacdExitConditionsBar
                  exitConditions={{
                    currentPrice: tradeGroup.currentPrice,
                    stopLoss: tradeGroup.macdMomentumStopLoss,
                    profitTarget: tradeGroup.macdMomentumProfitTarget,
                    entryPrice: tradeGroup.macdMomentumEntryPrice,
                  }}
                />
              </div>
            )}

            {/* Watching State - No current position */}
            {tradeGroup.state === 'WATCHING' && (
              <div className="macd-watching">
                <div className="watching-message">
                  <p className="last-cycle-info">
                    {tradeGroup.macdMomentumCycleCount && tradeGroup.macdMomentumCycleCount > 0
                      ? 'Last cycle completed. Watching for the next bullish crossover with an expanding positive histogram.'
                      : 'Watching for a bullish crossover with an expanding positive histogram to enter.'}
                  </p>
                </div>
              </div>
            )}

            {/* Paused State */}
            {tradeGroup.state === 'PAUSED' && (
              <div className="macd-paused">
                <div className="paused-message">
                  <h4>⏸️ Strategy Paused</h4>
                  <p>Trading paused due to consecutive losses or daily loss limit</p>
                </div>
              </div>
            )}

            {/* Operator-Paused State: distinct from automated PAUSED.
                Will NOT auto-resume; must be resumed manually by an operator. */}
            {tradeGroup.state === 'OPERATOR_PAUSED' && (
              <div className="macd-paused">
                <div className="paused-message">
                  <h4>⏸️ Operator-Paused</h4>
                  <p>Manually paused by an operator. This group opens no new positions and will not auto-resume — it must be resumed manually.</p>
                </div>
              </div>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* VWAP Reversion Strategy Section - Only shown for VWAP_REVERSION type */}
      {tradeGroup.tradeGroupType === 'VWAP_REVERSION' && (
        <StandaloneSection title="VWAP Mean Reversion Strategy">
          <div className="card-body">
            {/* Entry-conditions bar — owns its own data fetch (TASK-040) */}
            {tradeGroup.state === 'WATCHING' && tradeGroupId && (
              <div className="vwap-entry-conditions">
                <h3 className="section-title">Entry Conditions</h3>
                <VwapEntryConditionsBar tradeGroupId={tradeGroupId} />
              </div>
            )}

            {/* Trade-group configuration */}
            <div className="position-details-grid">
              <div className="position-detail-card">
                <span className="detail-label">VWAP Period</span>
                <span className="detail-value">{tradeGroup.vwapPeriod ?? 'N/A'}</span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Band Sigma</span>
                <span className="detail-value">{tradeGroup.vwapBandSigma?.toFixed(2) ?? 'N/A'}</span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Mean Reversion Period</span>
                <span className="detail-value">{tradeGroup.vwapStrategyMetricPeriod ?? 'N/A'}</span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Cycles</span>
                <span className="detail-value">
                  {tradeGroup.vwapCycleCount ?? 0}
                  {tradeGroup.vwapCycleCount ? (
                    <span className="detail-subtext">
                      {' '}({tradeGroup.vwapWinCount ?? 0}W / {tradeGroup.vwapLossCount ?? 0}L)
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Win Rate</span>
                <span className="detail-value">
                  {tradeGroup.vwapWinRate != null
                    ? `${(tradeGroup.vwapWinRate * 100).toFixed(1)}%`
                    : '—'}
                </span>
              </div>
            </div>

            {/* Current cycle context — populated when an entry has occurred (REQ-023) */}
            {tradeGroup.vwapCycleId && (
              <>
                <h3 className="section-title">Current Cycle</h3>

                {/* Visual exit-conditions bar when in position (operator parity with siblings) */}
                {(tradeGroup.vwapCycleState === 'IN_POSITION'
                  || tradeGroup.vwapCycleState === 'PARTIAL_EXIT'
                  || tradeGroup.vwapCycleState === 'PRIMARY_STOP_LOSS_PLACED'
                  || tradeGroup.vwapCycleState === 'PRIMARY_STOP_LOSS_PENDING') && (
                  <div className="vwap-exit-conditions-wrapper" style={{ marginBottom: '12px' }}>
                    <VwapExitConditionsBar
                      exitConditions={{
                        currentPrice: tradeGroup.currentPrice,
                        stopLoss: tradeGroup.vwapStopLoss,
                        stopLossLimit: tradeGroup.vwapStopLossLimit,
                        entryPrice: tradeGroup.vwapEntryPrice,
                        vwapTarget: tradeGroup.vwapEntryVwap,
                        isPartialExit: tradeGroup.vwapCycleState === 'PARTIAL_EXIT',
                      }}
                    />
                  </div>
                )}

                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">Cycle State</span>
                    <span className="detail-value">{tradeGroup.vwapCycleState ?? 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Price</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.vwapEntryPrice)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry VWAP</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.vwapEntryVwap)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Upper Band</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.vwapEntryUpperBand)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Lower Band</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.vwapEntryLowerBand)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Deviation</span>
                    <span className="detail-value">
                      {tradeGroup.vwapEntryVwapDeviationPct != null
                        ? `${(tradeGroup.vwapEntryVwapDeviationPct * 100).toFixed(4)}%`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Stop Loss</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.vwapStopLoss)}</span>
                    {tradeGroup.vwapStopLossLimit != null && (
                      <span className="detail-subtext">
                        Limit: {formatCurrency(tradeGroup.vwapStopLossLimit)}
                      </span>
                    )}
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Stop Basis</span>
                    <span className="detail-value">{formatStopBasis(tradeGroup.vwapStopBasis)}</span>
                    {tradeGroup.vwapEffectiveAtrMultiplier != null && (
                      <span className="detail-subtext">×{tradeGroup.vwapEffectiveAtrMultiplier} ATR</span>
                    )}
                    {formatStopTier(tradeGroup.vwapEffectiveStopTier) != null && (
                      <span className="detail-subtext">{formatStopTier(tradeGroup.vwapEffectiveStopTier)}</span>
                    )}
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Time</span>
                    <span className="detail-value">
                      {tradeGroup.vwapEntryTime ? new Date(tradeGroup.vwapEntryTime).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Partial exit progress — populated once partial exit has fired */}
                {tradeGroup.vwapPartialExitCompleted && (
                  <>
                    <h3 className="section-title">Partial Exit</h3>
                    <div className="position-details-grid">
                      <div className="position-detail-card highlight">
                        <span className="detail-label">Partial Exit Price</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.vwapPartialExitPrice)}</span>
                      </div>
                      <div className="position-detail-card highlight">
                        <span className="detail-label">Partial Exit VWAP</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.vwapPartialExitVwap)}</span>
                      </div>
                      <div className="position-detail-card highlight">
                        <span className="detail-label">Partial Exit Quote Size</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.vwapPartialExitQuoteSize)}</span>
                      </div>
                      <div className="position-detail-card highlight">
                        <span className="detail-label">Partial Exit Time</span>
                        <span className="detail-value">
                          {tradeGroup.vwapPartialExitTime ? new Date(tradeGroup.vwapPartialExitTime).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* Ichimoku Cloud Strategy Section - Only shown for ICHIMOKU_CLOUD type.
          PLAN-046: entry/exit bars are self-fetching; both share the same
          queryKey so TanStack dedupes the underlying request. Attribution panel
          mirrors VWAP/Bollinger structure but reads from the camelCase
          ichimokuDetails block produced by TradeGroupApiHandler.getIchimokuCloudDetails. */}
      {tradeGroup.tradeGroupType === 'ICHIMOKU_CLOUD' && (
        <StandaloneSection title="Ichimoku Cloud Strategy">
          <div className="card-body">
            {/* Entry conditions while watching */}
            {tradeGroup.state === 'WATCHING' && tradeGroupId && (
              <div className="ichimoku-entry-conditions">
                <h3 className="section-title">Entry Conditions</h3>
                <IchimokuEntryConditionsBar tradeGroupId={tradeGroupId} />
              </div>
            )}

            {/* Exit conditions while in position */}
            {(tradeGroup.state === 'IN_POSITION'
              || tradeGroup.state === 'PARTIAL_EXIT') && tradeGroupId && (
              <div className="ichimoku-exit-conditions" style={{ marginBottom: '12px' }}>
                <h3 className="section-title">Exit Conditions</h3>
                <IchimokuExitConditionsBar tradeGroupId={tradeGroupId} />
              </div>
            )}

            {/* Trade-group configuration */}
            {tradeGroup.ichimokuDetails && (
              <>
                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">Granularity</span>
                    <span className="detail-value">
                      {tradeGroup.ichimokuDetails.granularityMins != null
                        ? `${tradeGroup.ichimokuDetails.granularityMins}m`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Stop Style</span>
                    <span className="detail-value">{tradeGroup.ichimokuDetails.stopStyle ?? 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Position Size %</span>
                    <span className="detail-value">
                      {tradeGroup.ichimokuDetails.positionSizePct != null
                        ? `${(tradeGroup.ichimokuDetails.positionSizePct * 100).toFixed(2)}%`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Cycles</span>
                    <span className="detail-value">
                      {tradeGroup.ichimokuDetails.cycleCount ?? 0}
                      {tradeGroup.ichimokuDetails.cycleCount ? (
                        <span className="detail-subtext">
                          {' '}({tradeGroup.ichimokuDetails.winCount ?? 0}W / {tradeGroup.ichimokuDetails.lossCount ?? 0}L)
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Cumulative P&amp;L</span>
                    <span className="detail-value">
                      {formatCurrency(tradeGroup.ichimokuDetails.cumulativePnl ?? 0)}
                    </span>
                  </div>
                </div>

                {/* Current cycle — entry attribution (REQ-016) */}
                {tradeGroup.ichimokuDetails.cycleId && (
                  <>
                    <h3 className="section-title">Current Cycle — Entry Snapshot</h3>
                    <div className="position-details-grid">
                      <div className="position-detail-card">
                        <span className="detail-label">Cycle State</span>
                        <span className="detail-value">{tradeGroup.ichimokuDetails.cycleState ?? 'N/A'}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Entry Price</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.entryPrice)}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Entry Time</span>
                        <span className="detail-value">
                          {tradeGroup.ichimokuDetails.entryTime
                            ? new Date(tradeGroup.ichimokuDetails.entryTime).toLocaleString()
                            : 'N/A'}
                        </span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Tenkan-sen</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.entryTenkanSen)}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Kijun-sen</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.entryKijunSen)}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Senkou Span A</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.entrySenkouSpanA)}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Senkou Span B</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.entrySenkouSpanB)}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Chikou Span</span>
                        <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.entryChikouSpan)}</span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Cloud Top / Bottom</span>
                        <span className="detail-value">
                          {formatCurrency(tradeGroup.ichimokuDetails.entryCloudTop)}
                          {' / '}
                          {formatCurrency(tradeGroup.ichimokuDetails.entryCloudBottom)}
                        </span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Stop-Loss Basis</span>
                        <span className="detail-value">
                          {formatCurrency(tradeGroup.ichimokuDetails.stopLossBasis)}
                        </span>
                      </div>
                      <div className="position-detail-card">
                        <span className="detail-label">Stop-Loss Price</span>
                        <span className="detail-value">
                          {formatCurrency(tradeGroup.ichimokuDetails.stopLossPrice)}
                        </span>
                      </div>
                    </div>

                    {/* Entry signal components — CSV from backend, render as badges */}
                    {tradeGroup.ichimokuDetails.entrySignalComponents && (
                      <div style={{ marginTop: '0.5rem' }}>
                        <span className="detail-label" style={{ marginRight: '0.5rem' }}>Entry Signal Components:</span>
                        {tradeGroup.ichimokuDetails.entrySignalComponents
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                          .map(component => (
                            <span
                              key={component}
                              className="status-badge"
                              style={{ marginRight: '0.25rem' }}
                            >
                              {component}
                            </span>
                          ))}
                      </div>
                    )}

                    {/* Exit attribution (REQ-019) — populated once cycle has exited */}
                    {tradeGroup.ichimokuDetails.exitTime && (
                      <>
                        <h3 className="section-title">Current Cycle — Exit Snapshot</h3>
                        <div className="position-details-grid">
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Price</span>
                            <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.exitPrice)}</span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Time</span>
                            <span className="detail-value">
                              {new Date(tradeGroup.ichimokuDetails.exitTime).toLocaleString()}
                            </span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Reason</span>
                            <span className="detail-value">{tradeGroup.ichimokuDetails.exitReason ?? 'N/A'}</span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Realized P&amp;L</span>
                            <span className="detail-value">
                              {formatCurrency(tradeGroup.ichimokuDetails.realizedPnl)}
                            </span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Tenkan-sen</span>
                            <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.exitTenkanSen)}</span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Kijun-sen</span>
                            <span className="detail-value">{formatCurrency(tradeGroup.ichimokuDetails.exitKijunSen)}</span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Senkou A / B</span>
                            <span className="detail-value">
                              {formatCurrency(tradeGroup.ichimokuDetails.exitSenkouSpanA)}
                              {' / '}
                              {formatCurrency(tradeGroup.ichimokuDetails.exitSenkouSpanB)}
                            </span>
                          </div>
                          <div className="position-detail-card">
                            <span className="detail-label">Exit Cloud Top / Bottom</span>
                            <span className="detail-value">
                              {formatCurrency(tradeGroup.ichimokuDetails.exitCloudTop)}
                              {' / '}
                              {formatCurrency(tradeGroup.ichimokuDetails.exitCloudBottom)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* Pivot Point Trading Strategy Section — single-position strategy (TASK-039b).
          Renders the pivot-prefixed detail block projected by
          TradeGroupApiHandler.getPivotPointDetails: cumulative performance, per-group
          config (pivot_point_trade_group), entry attribution + resolved PP/R1/R2 targets
          (pivot_point_cycle_context), and three-tier exit progress. Mirrors the altitude
          of the sibling Ichimoku/Bollinger panels — real API data only. */}
      {tradeGroup.tradeGroupType === 'PIVOT_POINT_TRADING' && (
        <StandaloneSection title="Pivot Point Trading Strategy">
          <div className="card-body">
            {/* Entry/exit condition indicators (UI-PPEC-001) — entry readiness
                (proximity to support + ambient conditions + confidence + cooldown);
                the exit-proximity bar self-renders only while holding a position.
                Both share one fetch. */}
            <div className="pivot-point-conditions-detail">
              <PivotPointEntryConditionsBar tradeGroupId={tradeGroup.tradeGroupId} />
              <PivotPointExitConditionsBar tradeGroupId={tradeGroup.tradeGroupId} />
            </div>
            {/* Cumulative performance */}
            <div className="position-details-grid">
              <div className="position-detail-card">
                <span className="detail-label">Cycle State</span>
                <span className="detail-value">{tradeGroup.state.replace(/_/g, ' ')}</span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Cycles</span>
                <span className="detail-value">
                  {tradeGroup.pivotCycleCount ?? 0}
                  {tradeGroup.pivotCycleCount ? (
                    <span className="detail-subtext">
                      {' '}({tradeGroup.pivotWinCount ?? 0}W / {tradeGroup.pivotLossCount ?? 0}L)
                    </span>
                  ) : null}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Win Rate</span>
                <span className="detail-value">
                  {tradeGroup.pivotWinRate != null
                    ? `${(tradeGroup.pivotWinRate * 100).toFixed(1)}%`
                    : 'N/A'}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Cumulative P&amp;L</span>
                <span className={`detail-value ${getProfitClass(tradeGroup.pivotCumulativePnl)}`}>
                  {formatCurrency(tradeGroup.pivotCumulativePnl ?? 0)}
                </span>
              </div>
            </div>

            {/* Per-group config (pivot_point_trade_group) */}
            <div className="position-details-grid" style={{ marginTop: '1rem' }}>
              <div className="position-detail-card">
                <span className="detail-label">Pivot Timeframe</span>
                <span className="detail-value">
                  {tradeGroup.pivotTimeframe?.replace(/_/g, ' ') ?? 'N/A'}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Granularity</span>
                <span className="detail-value">
                  {tradeGroup.pivotGranularityMins != null
                    ? `${tradeGroup.pivotGranularityMins}m`
                    : 'N/A'}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Enabled Support Levels</span>
                <span className="detail-value">
                  {[
                    tradeGroup.pivotTradeS1 ? 'S1' : null,
                    tradeGroup.pivotTradeS2 ? 'S2' : null,
                    tradeGroup.pivotTradeS3 ? 'S3' : null,
                  ].filter(Boolean).join(', ') || 'None'}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Tiered Exit</span>
                <span className="detail-value">
                  {tradeGroup.pivotTieredExitEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Tier Exit % (PP / R1 / R2)</span>
                <span className="detail-value">
                  {tradeGroup.pivotTierExitPctPivot != null
                    ? `${(tradeGroup.pivotTierExitPctPivot * 100).toFixed(0)}%`
                    : 'N/A'}
                  {' / '}
                  {tradeGroup.pivotTierExitPctR1 != null
                    ? `${(tradeGroup.pivotTierExitPctR1 * 100).toFixed(0)}%`
                    : 'N/A'}
                  {' / '}
                  {tradeGroup.pivotTierExitPctR2 != null
                    ? `${(tradeGroup.pivotTierExitPctR2 * 100).toFixed(0)}%`
                    : 'N/A'}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Stop Loss %</span>
                <span className="detail-value">
                  {tradeGroup.pivotStopLossPct != null
                    ? `${(tradeGroup.pivotStopLossPct * 100).toFixed(2)}%`
                    : 'N/A'}
                </span>
              </div>
            </div>

            {/* Current cycle: entry attribution + resolved targets + exit progress */}
            {tradeGroup.pivotCycleId && (
              <>
                <h4 className="detail-card-title" style={{ marginTop: '1.5rem' }}>
                  Current Cycle #{tradeGroup.pivotCycleNumber} — Entry Attribution
                </h4>
                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Support Level</span>
                    <span className="detail-value">{tradeGroup.pivotEntryLevelType ?? 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Signal Strength</span>
                    <span className="detail-value">
                      {tradeGroup.pivotEntrySignalStrength != null
                        ? tradeGroup.pivotEntrySignalStrength.toFixed(3)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Confidence Score</span>
                    <span className="detail-value">
                      {tradeGroup.pivotConfidenceScore != null
                        ? tradeGroup.pivotConfidenceScore
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry RSI</span>
                    <span className="detail-value">
                      {tradeGroup.pivotEntryRsi != null
                        ? tradeGroup.pivotEntryRsi.toFixed(2)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Market Regime</span>
                    <span className="detail-value">{tradeGroup.pivotEntryMarketRegime ?? 'N/A'}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Price</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.pivotEntryPrice)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Time</span>
                    <span className="detail-value">
                      {tradeGroup.pivotEntryTime
                        ? new Date(tradeGroup.pivotEntryTime).toLocaleString()
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Stop Loss</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.pivotStopLossPrice)}</span>
                  </div>
                </div>

                <h4 className="detail-card-title" style={{ marginTop: '1.5rem' }}>
                  Three-Tier Exit Ladder
                </h4>
                <div className="position-details-grid">
                  <div className="position-detail-card">
                    <span className="detail-label">
                      Tier 1 — PP Target {tradeGroup.pivotPartialExitCompleted ? '✓' : '○'}
                    </span>
                    <span className="detail-value">
                      {formatCurrency(tradeGroup.pivotTargetPivot)}
                      {tradeGroup.pivotPartialExitCompleted && tradeGroup.pivotPartialExitPrice != null && (
                        <span className="detail-subtext">
                          {' '}(filled @ {formatCurrency(tradeGroup.pivotPartialExitPrice)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">
                      Tier 2 — R1 Target {tradeGroup.pivotSecondPartialCompleted ? '✓' : '○'}
                    </span>
                    <span className="detail-value">
                      {formatCurrency(tradeGroup.pivotTargetR1)}
                      {tradeGroup.pivotSecondPartialCompleted && tradeGroup.pivotSecondPartialPrice != null && (
                        <span className="detail-subtext">
                          {' '}(filled @ {formatCurrency(tradeGroup.pivotSecondPartialPrice)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Tier 3 — R2 Target</span>
                    <span className="detail-value">{formatCurrency(tradeGroup.pivotTargetR2)}</span>
                  </div>
                  <div className="position-detail-card">
                    <span className="detail-label">Entry Filled Size</span>
                    <span className="detail-value">
                      {tradeGroup.pivotEntryFilledSize != null
                        ? tradeGroup.pivotEntryFilledSize
                        : 'N/A'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </StandaloneSection>
      )}

      {/* Moving Average Crossover Strategy Section — shown only for MA_CROSSOVER type.
          Per-cycle MA snapshot fields are not in the active-trade-groups list payload;
          this section surfaces the cumulative performance available generically plus the
          MA-overlay chart with crossover markers and a trailing-stop reference line
          (MaCrossoverChart fetches the real candle + SMA/EMA series — no fake data). */}
      {tradeGroup.tradeGroupType === 'MA_CROSSOVER' && (
        <StandaloneSection title="Moving Average Crossover Strategy">
          <div className="card-body">
            {/* Cumulative performance (generic single-position fields) */}
            <div className="position-details-grid">
              <div className="position-detail-card">
                <span className="detail-label">Cycle State</span>
                <span className="detail-value">{tradeGroup.state.replace(/_/g, ' ')}</span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Combined P&amp;L</span>
                <span className={`detail-value ${getProfitClass(tradeGroup.combinedPnl)}`}>
                  {formatCurrency(tradeGroup.combinedPnl)}
                </span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Current Price</span>
                <span className="detail-value">{formatCurrency(tradeGroup.currentPrice)}</span>
              </div>
              <div className="position-detail-card">
                <span className="detail-label">Allocated Capital</span>
                <span className="detail-value">
                  {tradeGroup.originalQuoteSize != null
                    ? formatCurrency(tradeGroup.originalQuoteSize)
                    : 'N/A'}
                </span>
              </div>
            </div>

            {/* MA-overlay chart: fast/slow MA + crossover markers + trailing-stop reference.
                Periods default to the flagship 50/200 EMA; the chart reads real series at
                the detail view's selected granularity. */}
            <h4 className="detail-card-title" style={{ marginTop: '1.5rem' }}>
              MA Overlay &amp; Crossovers
            </h4>
            <MaCrossoverChart
              productId={tradeGroup.productId}
              granularityMins={granularity}
              maType="EMA"
              fastPeriod={50}
              slowPeriod={200}
              trailingStopPct={0.08}
            />
          </div>
        </StandaloneSection>
      )}

      {/* Volatility Ladder Attributes - Show only for VOLATILITY_LADDER trade groups */}
      {tradeGroup.tradeGroupType === 'VOLATILITY_LADDER' && tradeGroup.volatilityLadder && (
        <StandaloneSection title="Volatility Ladder Configuration & State">
          <div className="card-body">
            <VolatilityAttributesSection 
              volatilityLadder={tradeGroup.volatilityLadder}
              originalQuoteSize={tradeGroup.originalQuoteSize}
            />
          </div>
        </StandaloneSection>
      )}

      {/* Trade Group Orchestrator Section - Only shown for ORCHESTRATED_LADDER type.
          Surfaces the persisted orchestrator lifecycle state, managed child ladder id,
          aggregate PnL, last confluence score, and deployed-config snapshot from the
          camelCase orchestratedLadder block projected by
          TradeGroupApiHandler.mapOrchestratedLadderRow (TASK-031/TASK-032). Mirrors the
          sibling Ichimoku/Volatility-Ladder detail panels. */}
      {tradeGroup.tradeGroupType === 'ORCHESTRATED_LADDER' && tradeGroup.orchestratedLadder && (
        <StandaloneSection title="Trade Group Orchestrator">
          <div className="card-body">
            <OrchestratedLadderSection orchestratedLadder={tradeGroup.orchestratedLadder} />
          </div>
        </StandaloneSection>
      )}

      {/* Tabbed Interface - Order History, Adjustment Log, Ladder Ranges, Timeline */}
      <StandaloneSection title="">
        <div className="card-body">
          <TradeGroupTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={[
              { id: 'orders', label: 'Order History', count: orders.length },
              // Only show Adjustment Log, Ladder Ranges, and Activity Timeline for Volatility Ladder
              ...(tradeGroup.tradeGroupType === 'VOLATILITY_LADDER' ? [
                { id: 'adjustments' as TradeGroupTabId, label: 'Adjustment Log', count: 0 },
                { id: 'ranges' as TradeGroupTabId, label: 'Ladder Ranges', count: 0 },
              ] : []),
              { id: 'timeline', label: 'Price Action' },
              ...(tradeGroup.tradeGroupType === 'VOLATILITY_LADDER' ? [
                { id: 'activity' as TradeGroupTabId, label: 'Activity Timeline' },
              ] : []),
            ]}
          />

          <div
            className="trade-group-tab-content"
            role="tabpanel"
            id={`trade-group-panel-${activeTab}`}
            aria-labelledby={`trade-group-tab-${activeTab}`}
          >
            {activeTab === 'orders' && (
              SINGLE_POSITION_TYPES.has(tradeGroup.tradeGroupType) ? (
                <SinglePositionCycleHistory tradeGroupId={tradeGroupId!} currentPrice={tradeGroup.currentPrice} />
              ) : (
                <OrdersTable orders={orders} currentPrice={tradeGroup.currentPrice} />
              )
            )}

            {activeTab === 'adjustments' && (
              <AdjustmentLogTable tradeGroupId={tradeGroupId!} />
            )}

            {activeTab === 'ranges' && (
              <LadderRangesTable tradeGroupId={tradeGroupId!} productId={tradeGroup.productId} />
            )}

            {activeTab === 'timeline' && (
              <>
                <GranularityDateRangeControls
                  selectedTimeframe={selectedTimeframe}
                  onTimeframeChange={setSelectedTimeframe}
                />
                <TimelineChart
                  tradeGroupId={tradeGroupId!}
                  productId={tradeGroup.productId}
                  tradeGroupCreatedAt={tradeGroup.createdAt ?? Date.now()}
                  granularityMins={granularity}
                  startDate={dateRange.start_date}
                  endDate={dateRange.end_date}
                />
              </>
            )}

            {activeTab === 'activity' && (
              <TradeActivityTimeline
                tradeGroupId={tradeGroupId!}
                productId={tradeGroup.productId}
                tradeGroupCreatedAt={tradeGroup.createdAt ?? undefined}
              />
            )}
          </div>
        </div>
      </StandaloneSection>

      {/* Cancel Trade Group Modal */}
      {showCancelModal && (
        <CancelTradeGroupModal
          tradeGroupId={tradeGroupId!}
          productId={tradeGroup.productId}
          onClose={() => setShowCancelModal(false)}
          onSuccess={() => {
            alert('Trade group orders cancelled successfully!');
          }}
        />
      )}

      {pauseResumeAction && (
        <OperatorPauseResumeModal
          tradeGroupId={tradeGroupId!}
          action={pauseResumeAction}
          onClose={() => setPauseResumeAction(null)}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
};

export default TradeGroupDetail;
